import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
} from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import ApiIcon from '@mui/icons-material/Api';
import FunctionsIcon from '@mui/icons-material/Functions';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAuth } from '../context/AuthContext';
import useFlowBuilderStore, {
  createStarterGraph,
  hydrateFlowFromApi,
} from '../store/flowBuilderStore';
import { nodeTypes } from '../components/flow/CustomNode';
import PremiumEdge from '../components/flow/PremiumEdge';
import useEditorHistory from '../components/flow/useEditorHistory';
import { previewConfig, editorWarnings } from '../components/flow/editorTools';
import { searchFlowNodes } from '../utils/flowNodeSearch';
import NodeSelectionDrawer from '../components/flow/NodeSelectionDrawer';
import FlowPropertiesPanel from '../components/flow/FlowPropertiesPanel';
import FlowDebuggerPanel from '../components/flow/FlowDebuggerPanel';
import DynamicChatWorkspace from '../components/flow/DynamicChatWorkspace';
import { findFlowNodeItem } from '../components/flow/flowNodeCatalog';
import { getOrCreateWebUid, generateSessionId } from '../utils/web-uid';

/** Purpose: parse JSON editor input without throwing into the render cycle. Why: incomplete JSON is a normal intermediate state during flow configuration. */
const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

/** Purpose: create an identifier for a newly connected canvas edge. Why: React Flow needs unique IDs to preserve selection and branch identity. */
const makeEdgeId = (source, target) => `edge-${source}-${target}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
const edgeTypes = { premium: PremiumEdge };

/** Purpose: normalize quick-reply choices from a node config. Why: branch rendering and edge creation must use the same option list. */
const getQuickReplyOptions = (sourceNode) => {
  const options = sourceNode?.data?.config?.options;
  return Array.isArray(options)
    ? options.map((option, index) => String(
        typeof option === 'object' && option !== null
          ? option.text ?? option.value ?? option.title ?? option.label ?? `Option ${index + 1}`
          : option ?? ''
      ).trim()).filter(Boolean)
    : [];
};

/** Purpose: decide whether a node creates option-specific outgoing branches. Why: only quick-reply nodes should interpret handles as answer choices. */
const isQuickReplySourceNode = (sourceNode) => {
  const libraryKey = sourceNode?.data?.libraryKey || '';
  const config = sourceNode?.data?.config || {};
  return libraryKey === 'quick_replies'
    || libraryKey === 'message_quick_replies'
    || config.variable === 'quick_reply'
    || config.validation === 'quick_reply'
    || (Array.isArray(config.options) && config.options.length > 0);
};

/** Purpose: map a canvas handle back to its quick-reply option. Why: connections must preserve which exact answer the user selected. */
const getQuickReplyOptionForHandle = (sourceNode, sourceHandle = '') => {
  const match = String(sourceHandle || '').match(/^quick-reply-(\d+)$/);
  if (!match) return '';
  return getQuickReplyOptions(sourceNode)[Number(match[1])] || '';
};

/** Purpose: detect the special quick-reply fallback handle. Why: unmatched input needs a distinct branch from configured answer choices. */
const isQuickReplyFallbackHandle = (sourceHandle = '') => String(sourceHandle || '') === 'quick-reply-fallback';

// const getQuickReplySourceHandle = (sourceNode, edge) => {
//   if (!isQuickReplySourceNode(sourceNode)) return edge?.sourceHandle || '';
//   const condition = String(edge?.data?.condition || edge?.label || '').trim();
//   if (condition.toLowerCase() === 'fallback') return 'quick-reply-fallback';
//   const index = getQuickReplyOptions(sourceNode).findIndex((option) => option.toLowerCase() === condition.toLowerCase());
//   // Older flows could persist the wrong sourceHandle. The selected button
//   // label is authoritative, so it always wins when it is available.
//   return index >= 0 ? `quick-reply-${index}` : (edge?.sourceHandle || '');
// };

/** Purpose: recover a quick-reply source handle from saved edge data. Why: older flows may store branch metadata differently from newly created edges. */
const getQuickReplySourceHandle = (sourceNode, edge) => {
  if (!isQuickReplySourceNode(sourceNode)) return edge?.sourceHandle || '';

  const condition = String(
    edge?.data?.condition || edge?.label || ''
  ).trim();

  if (condition.toLowerCase() === 'fallback') {
    return 'quick-reply-fallback';
  }

  /** Purpose: Find the quick-reply option matching a stored condition. Why: Case-insensitive matching preserves routes when option capitalization changes. */
  const index = getQuickReplyOptions(sourceNode).findIndex(
    (option) =>
      option.toLowerCase() === condition.toLowerCase()
  );

  return index >= 0
    ? `quick-reply-${index}`
    : (edge?.sourceHandle || '');
};

/** Purpose: infer the resolved or fallback route for a handoff edge. Why: handoff outcomes must remain distinct in both canvas display and saved flow connections. */
const getHandoffEdgeBranch = (sourceNode, edge, allEdges = []) => {
  if (sourceNode?.data?.libraryKey !== 'chat_handoff') return null;
  const condition = String(edge?.data?.condition || edge?.label || '').trim().toLowerCase();
  const sourceHandle = String(edge?.sourceHandle || '').trim().toLowerCase();
  if (condition === 'handoff_timeout' || condition.includes('timeout') || sourceHandle === 'handoff-timeout') {
    return { sourceHandle: 'handoff-timeout', condition: 'handoff_timeout', label: 'Timeout / No agent' };
  }
  if (condition === 'resolved' || sourceHandle === 'handoff-resolved') {
    return { sourceHandle: 'handoff-resolved', condition: 'resolved', label: 'Resolved' };
  }

  // Old Chat Handoff edges were stored as generic `next`. If another edge is
  // already the resolved route, this generic edge is the second/fallback path.
  /** Purpose: Resolve sibling has resolved in the flow builder module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
  const siblingHasResolved = allEdges.some((candidate) => {
    if (candidate === edge || candidate.source !== edge.source) return false;
    const candidateCondition = String(candidate?.data?.condition || candidate?.label || '').trim().toLowerCase();
    return candidateCondition === 'resolved' || candidate.sourceHandle === 'handoff-resolved';
  });
  return siblingHasResolved
    ? { sourceHandle: 'handoff-timeout', condition: 'handoff_timeout', label: 'Timeout / No agent' }
    : { sourceHandle: 'handoff-resolved', condition: 'resolved', label: 'Resolved' };
};

/** Purpose: identify success/failure or conversation branches on API-like nodes. Why: validation and canvas labels depend on the execution outcome represented by an edge. */
const getApiEdgeBranch = (sourceNode, edge = {}) => {
  const isDynamicChat = sourceNode?.data?.libraryKey === 'dynamic_chat';
  if (!isDynamicChat && (sourceNode?.type !== 'api_call' || sourceNode?.data?.libraryKey === 'function')) return null;
  const condition = String(edge?.data?.condition || edge?.label || '').trim().toLowerCase();
  const sourceHandle = String(edge?.sourceHandle || '').trim().toLowerCase();
  // A persisted edge may carry a generic handle while its condition/label names
  // the conversation outcome. Check both fields so `success` cannot mask an
  // explicit Dynamic Chat `exit` or `handoff` route.
  /** Purpose: Resolve conversation branch in the flow builder module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
  const conversationBranch = [sourceHandle, condition].find((value) => ['exit', 'handoff'].includes(value));
  if (isDynamicChat && sourceNode?.data?.config?.conversationMode && conversationBranch) {
    const branch = conversationBranch;
    return { sourceHandle: branch, condition: branch, label: branch === 'exit' ? 'Exit' : 'Handoff', color: '#a78bfa' };
  }
  const isFailure = ['failure', 'error', 'false'].includes(condition) || sourceHandle === 'failure';
  return isFailure
    ? { sourceHandle: 'failure', condition: 'failure', label: 'Failure', color: '#fb7185' }
    : { sourceHandle: 'success', condition: 'success', label: 'Success', color: '#34d399' };
};

/** Purpose: build normalized data after a canvas connection is made. Why: special branches need labels/conditions compatible with serialization and runtime execution. */
const buildEdge = ({ source, target, sourceHandle = '', sourceNode, edges }) => {
  const outgoingCount = edges.filter((edge) => edge.source === source).length;
  const quickReplyOptions = getQuickReplyOptions(sourceNode);
  const selectedQuickReplyOption = getQuickReplyOptionForHandle(sourceNode, sourceHandle);
  /** Purpose: Find the first quick reply without a route. Why: New connections should use an unused answer rather than duplicate an existing branch. */
  const nextQuickReplyOption = quickReplyOptions.find((option) => (
    !edges.some((edge) => edge.source === source && String(edge.data?.condition || edge.label || '').trim().toLowerCase() === option.toLowerCase())
  ));
  const isApiSourceNode = (sourceNode?.type === 'api_call' && sourceNode?.data?.libraryKey !== 'function') || sourceNode?.data?.libraryKey === 'dynamic_chat';
  const isChatHandoffSourceNode = sourceNode?.data?.libraryKey === 'chat_handoff';
  const isAbSplitSource = sourceNode?.data?.libraryKey === 'ab_split' || sourceNode?.data?.config?.uiNodeKey === 'ab_split';
  const isQuickReplyFallback = isQuickReplySourceNode(sourceNode) && isQuickReplyFallbackHandle(sourceHandle);
  const apiBranch = sourceNode?.data?.libraryKey === 'dynamic_chat' && sourceNode?.data?.config?.conversationMode && ['exit', 'handoff'].includes(sourceHandle)
    ? sourceHandle : String(sourceHandle || '').toLowerCase() === 'failure' ? 'failure' : 'success';
  const abBranch = sourceHandle === 'path_b' ? 'path_b' : (sourceHandle === 'path_a' ? 'path_a' : (outgoingCount === 0 ? 'path_a' : 'path_b'));
  const abColor = abBranch === 'path_a' ? '#38bdf8' : '#ec4899';
  const conditionBranch = sourceNode?.type === 'condition'
    ? (sourceHandle === 'condition-else' ? 'else' : sourceHandle || `condition-${outgoingCount}`)
    : '';
  const branchLabel = isAbSplitSource
    ? (abBranch === 'path_a' ? 'Path A' : 'Path B')
    : isApiSourceNode
    ? ({ failure: 'Failure', success: 'Success', exit: 'Exit', handoff: 'Handoff' }[apiBranch])
    : isChatHandoffSourceNode
      ? (sourceHandle === 'handoff-timeout' ? 'Timeout / No agent' : 'Resolved')
    : isQuickReplyFallback
      ? 'Fallback'
    : sourceNode?.type === 'condition'
    ? conditionBranch === 'else' ? 'ELSE' : conditionBranch.replace('condition-', 'IF ')
    : isQuickReplySourceNode(sourceNode) && (selectedQuickReplyOption || nextQuickReplyOption)
      ? selectedQuickReplyOption || nextQuickReplyOption
    : 'next';
  const condition = isAbSplitSource
    ? abBranch
    : isApiSourceNode
    ? apiBranch
    : isChatHandoffSourceNode
      ? (sourceHandle === 'handoff-timeout' ? 'handoff_timeout' : 'resolved')
    : isQuickReplyFallback
      ? 'fallback'
    : sourceNode?.type === 'condition'
    ? conditionBranch
    : isQuickReplySourceNode(sourceNode) && (selectedQuickReplyOption || nextQuickReplyOption)
      ? selectedQuickReplyOption || nextQuickReplyOption
    : 'next';
  const edgeColor = isAbSplitSource ? abColor : isApiSourceNode ? (apiBranch === 'failure' ? '#fb7185' : '#34d399') : '#67e8f9';

  return {
    id: makeEdgeId(source, target),
    source,
    target,
    sourceHandle,
    label: branchLabel,
    type: 'premium',
    animated: sourceNode?.type !== 'start',
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: edgeColor,
    },
    style: {
      strokeWidth: 2,
      stroke: edgeColor,
    },
    data: {
      condition,
    },
  };
};

/** Purpose: return presentation color for a canvas node type. Why: node visuals should share consistent type colors instead of duplicating style rules. */
const getNodeStyle = (type) => {
  const map = {
    start: '#34d399',
    end: '#fb7185',
    message: '#38bdf8',
    condition: '#a78bfa',
    input: '#fbbf24',
  };
  return map[type] || '#94a3b8';
};

/** Purpose: estimate visits, retention, and drop-off for the flow heatmap. Why: authors can visually inspect graph structure and branch distribution without editing saved flow data. */
const computeHeatmapMetrics = (nodes = [], edges = []) => {
  const inDegree = new Map();
  const outgoingMap = new Map();
  nodes.forEach((n) => {
    inDegree.set(n.id, 0);
    outgoingMap.set(n.id, []);
  });
  edges.forEach((e) => {
    inDegree.set(e.target, (inDegree.get(e.target) || 0) + 1);
    const list = outgoingMap.get(e.source) || [];
    list.push(e);
    outgoingMap.set(e.source, list);
  });

  const nodeMetrics = new Map();
  const edgeMetrics = new Map();
  const BASE_VISITS = 1250;
  const queue = [];

  nodes.forEach((n) => {
    if (n.type === 'start' || (inDegree.get(n.id) || 0) === 0) {
      nodeMetrics.set(n.id, {
        visits: BASE_VISITS,
        retention: '100%',
        dropOffCount: Math.round(BASE_VISITS * 0.04),
        dropOffRate: '4%',
      });
      queue.push(n.id);
    }
  });

  const visited = new Set(queue);
  while (queue.length > 0) {
    const currentId = queue.shift();
    const currentMetric = nodeMetrics.get(currentId) || { visits: BASE_VISITS, retention: '100%', dropOffCount: 40, dropOffRate: '4%' };
    const outgoing = outgoingMap.get(currentId) || [];
    if (outgoing.length === 0) continue;

    /** Purpose: Resolve the current traversal node. Why: Traversal needs the node type and configuration to interpret outgoing routes. */
    const sourceNode = nodes.find((n) => n.id === currentId);
    const isAbSplit = sourceNode?.data?.libraryKey === 'ab_split' || sourceNode?.data?.config?.uiNodeKey === 'ab_split';
    const ratioA = Number.isFinite(Number(sourceNode?.data?.config?.ratioA)) ? Number(sourceNode?.data?.config?.ratioA) : 50;
    const proceedingVisits = Math.max(0, currentMetric.visits - currentMetric.dropOffCount);

    outgoing.forEach((edge) => {
      let branchFraction = 1 / outgoing.length;
      if (isAbSplit && outgoing.length === 2) {
        if (edge.sourceHandle === 'path_a' || edge.data?.condition === 'path_a') {
          branchFraction = ratioA / 100;
        } else {
          branchFraction = (100 - ratioA) / 100;
        }
      }
      const edgeTraversals = Math.round(proceedingVisits * branchFraction);
      const edgePct = Math.round(branchFraction * 100);
      edgeMetrics.set(edge.id, {
        traversals: edgeTraversals,
        percentage: edgePct,
      });

      const existingTargetMetric = nodeMetrics.get(edge.target);
      const incomingVisits = (existingTargetMetric?.visits || 0) + edgeTraversals;
      const targetRetention = Math.round((incomingVisits / BASE_VISITS) * 100);
      const targetDropCount = Math.round(incomingVisits * (0.05 + (0.01 * (incomingVisits % 5))));
      const targetDropRate = incomingVisits > 0 ? Math.round((targetDropCount / incomingVisits) * 100) : 0;

      nodeMetrics.set(edge.target, {
        visits: incomingVisits,
        retention: `${targetRetention}%`,
        dropOffCount: targetDropCount,
        dropOffRate: `${targetDropRate}%`,
      });

      if (!visited.has(edge.target)) {
        visited.add(edge.target);
        queue.push(edge.target);
      }
    });
  }

  nodes.forEach((n) => {
    if (!nodeMetrics.has(n.id)) {
      nodeMetrics.set(n.id, {
        visits: 0,
        retention: '0%',
        dropOffCount: 0,
        dropOffRate: '0%',
      });
    }
  });

  return { nodeMetrics, edgeMetrics };
};

const HOME_COMMANDS = new Set(['home', '/home', 'start', 'menu', 'reset']);

/** Purpose: recognize supported home/reset commands during preview. Why: debug sessions should simulate chat runtime navigation shortcuts. */
const isHomeCommand = (value) => HOME_COMMANDS.has(String(value || '').trim().toLowerCase());

const debugToneClass = {
  error: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200',
  warning: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-200',
  info: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-200',
};

/** Purpose: select a severity style for a debug event. Why: errors, warnings, and successful steps must be distinguishable while scanning logs. */
const getDebugTone = (log = {}) => {
  const level = String(log.level || '').toLowerCase();
  if (level === 'error' || log.event === 'preview_request_error' || log.event === 'preview_home_error') return 'error';
  if (level === 'warn' || level === 'warning') return 'warning';
  if (String(log.event || '').includes('success') || String(log.event || '').includes('completed')) return 'success';
  return 'info';
};

/** Purpose: build a concise human-readable log title. Why: raw engine event identifiers are less scannable than a formatted summary. */
const formatDebugTitle = (log = {}) => {
  if (log.message) return log.message;
  if (log.event) return String(log.event).replace(/_/g, ' ');
  return 'Flow event';
};

/** Purpose: summarize node and timing details for a log row. Why: key context is visible without expanding the full event payload. */
const formatDebugMeta = (log = {}) => {
  const bits = [];
  if (log.nodeType) bits.push(log.nodeType);
  if (log.details?.nodeId) bits.push(log.details.nodeId);
  if (log.timestamp) bits.push(new Date(log.timestamp).toLocaleTimeString());
  return bits.join(' • ');
};

/** Purpose: render nested diagnostic values as readable text. Why: debug payloads can include objects that are otherwise difficult to inspect. */
const formatLogValue = (value) => {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') {
    const parsed = safeParseJson(value);
    return parsed ? JSON.stringify(parsed, null, 2) : value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

/** Purpose: decide whether a structured log can be inspected. Why: even compact event rows have useful metadata worth exposing on demand. */
const hasLogDetails = (log = {}) => {
  // Every structured event has useful top-level metadata, even when a node emitted no extra details.
  return Boolean(log && typeof log === 'object' && Object.keys(log).length);
};

/** Purpose: correlate a log row with its recorded node execution snapshot. Why: many node types put inputs/outputs on steps rather than the compact event log. */
const findRelatedStep = (log = {}, steps = []) => {
  if (!log.nodeId || !Array.isArray(steps)) return null;
  /** Purpose: Match execution steps to this log's node. Why: A node can run more than once, so log enrichment must choose from its executions. */
  const candidates = steps.filter((step) => String(step.nodeId || '') === String(log.nodeId));
  if (!candidates.length) return null;
  const logTime = Date.parse(log.timestamp || '');
  if (!Number.isFinite(logTime)) return candidates[candidates.length - 1];
  return candidates.reduce((closest, step) => {
    const distance = Math.abs(Date.parse(step.timestamp || '') - logTime);
    const closestDistance = Math.abs(Date.parse(closest.timestamp || '') - logTime);
    return distance < closestDistance ? step : closest;
  });
};

/** Purpose: render one labeled value in an expanded debug entry. Why: nested request/response details need a consistent readable layout. */
function LogDetailBlock({ title, value }) {
  if (value === undefined || value === null || value === '') return null;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/70">
      <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
        {title}
      </div>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-xs leading-5 text-slate-800 dark:text-slate-100">
        {formatLogValue(value)}
      </pre>
    </div>
  );
}

/** Purpose: render the expandable diagnostic payload for one log row. Why: keep the console compact while retaining full debugging evidence on demand. */
function LogDetails({ log, step }) {
  const details = log?.details && typeof log.details === 'object' ? log.details : {};
  const functionLogs = Array.isArray(details.functionLogs) ? details.functionLogs : [];
  const responseValue = details.response || (
    details.responsePreview || details.responseMeta
      ? {
        status: details.responseMeta?.statusCode || details.statusCode || null,
        bodyPreview: details.responsePreview,
        meta: details.responseMeta || null,
      }
      : null
  );
  const simpleDetails = Object.fromEntries(
    Object.entries(details).filter(([key]) => !['request', 'response', 'functionLogs', 'output'].includes(key))
  );

  const formattedFunctionLogs = functionLogs.length > 0
    ? functionLogs.map((item) => `[${String(item?.level || 'log').toUpperCase()}] ${typeof item?.message === 'string' ? item.message : JSON.stringify(item?.message ?? item)}`).join('\n')
    : (['custom_function_complete', 'custom_function_error'].includes(log?.event) ? '(No console output emitted)' : null);

  const functionOutputValue = details.output !== undefined
    ? (details.output === null ? 'null' : details.output)
    : (['custom_function_complete'].includes(log?.event) ? 'null' : null);

  return (
    <div className="mt-3 space-y-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-slate-950/40">
      <LogDetailBlock title="Event summary" value={{ event: log?.event, level: log?.level, message: log?.message, nodeId: log?.nodeId, nodeType: log?.nodeType, traceId: log?.traceId, timestamp: log?.timestamp }} />
      <LogDetailBlock title="Node execution snapshot" value={step} />
      <div className="grid gap-3 lg:grid-cols-2">
        <LogDetailBlock title="API Request" value={details.request} />
        <LogDetailBlock title="API Response" value={responseValue} />
      </div>
      <LogDetailBlock title="Function output" value={functionOutputValue} />
      <LogDetailBlock title="Function Console" value={formattedFunctionLogs} />
      <LogDetailBlock title="Details" value={Object.keys(simpleDetails).length ? simpleDetails : null} />
      <LogDetailBlock title="Raw Log" value={log} />
    </div>
  );
}

/** Purpose: display and control flow execution logs in the editor. Why: authors need step-by-step feedback to diagnose preview/test runs. */
function DebugConsolePane({
  logs = [],
  steps = [],
  sessionId = '',
  logHeight = 320,
  onRefreshLogs = null,
  logsRefreshing = false,
}) {
  const [expandedLogKey, setExpandedLogKey] = useState('');

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white/80 shadow-[0_16px_50px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-950/70 dark:shadow-2xl dark:shadow-slate-950/50">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-white/10">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-slate-500 dark:text-slate-500">Console</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">Logs</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            {logs.length} entries
          </div>
          <button
            type="button"
            onClick={() => onRefreshLogs?.()}
            disabled={!onRefreshLogs || logsRefreshing}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-700 transition hover:border-cyan-300 hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100"
          >
            <RefreshIcon className={logsRefreshing ? 'animate-spin' : ''} fontSize="inherit" />
            {logsRefreshing ? 'Refreshing' : 'Refresh logs'}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-5">
        {logs.length === 0 ? (
          <div className="flex min-h-[220px] items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-white/70 px-6 text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
            No logs yet. Use Home or Send, and the output will appear here.
          </div>
        ) : (
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
            <div className="grid grid-cols-[140px_120px_minmax(0,1fr)] gap-3 rounded-2xl bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:bg-white/5 dark:text-slate-500">
              <div>Time</div>
              <div>Trace</div>
              <div>Log</div>
            </div>
            {logs.map((log, index) => {
                const tone = getDebugTone(log);
                const logKey = `${log.timestamp || 'log'}-${index}`;
                const expanded = expandedLogKey === logKey;
                const expandable = hasLogDetails(log);
                const relatedStep = findRelatedStep(log, steps);
                return (
                  <div
                    key={logKey}
                    role={expandable ? 'button' : undefined}
                    tabIndex={expandable ? 0 : undefined}
                    aria-expanded={expandable ? expanded : undefined}
                    className={[
                    'rounded-2xl border px-4 py-3 text-sm shadow-sm transition',
                    expandable ? 'cursor-pointer hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500' : '',
                    debugToneClass[tone],
                  ].join(' ')}
                    onClick={() => {
                      if (!expandable) return;
                      setExpandedLogKey((current) => (current === logKey ? '' : logKey));
                    }}
                    onKeyDown={(event) => {
                      if (expandable && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        setExpandedLogKey((current) => (current === logKey ? '' : logKey));
                      }
                    }}
                >
                  <div className="grid grid-cols-[140px_120px_minmax(0,1fr)_auto] gap-3">
                    <div className="font-medium">
                      {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : '—'}
                    </div>
                    <div className="font-mono text-xs font-medium uppercase tracking-[0.18em]">
                      {log.traceId ? String(log.traceId).slice(-8) : sessionId ? sessionId.slice(-8) : '—'}
                    </div>
                    <div>
                      <div className="font-semibold">{formatDebugTitle(log)}</div>
                      {formatDebugMeta(log) ? (
                        <div className="mt-1 text-xs opacity-80">{formatDebugMeta(log)}</div>
                      ) : null}
                    </div>
                    {expandable ? (
                      <div className="self-start rounded-full border border-current/20 px-2 py-1 text-[11px] font-semibold">
                        {expanded ? 'Hide' : 'View'}
                      </div>
                    ) : null}
                  </div>
                  {expanded ? <LogDetails log={log} step={relatedStep} /> : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** Purpose: coordinate flow editing, canvas interactions, validation, preview, and persistence. Why: this page connects graph state to the surrounding workspace and backend APIs. */
export default function FlowBuilder() {
  const { flowId } = useParams();
  const [searchParams] = useSearchParams();
  const queryNodeId = searchParams.get('nodeId');
  const navigate = useNavigate();
  const { selectedBot, selectedEnv, selectedBotDetails, loading: authLoading } = useAuth();
  const isReadOnly = selectedEnv === 'prod';
  const editorHistory = useEditorHistory(`${selectedBot}/${selectedEnv}/${flowId}`, isReadOnly);
  const [canvasSearch, setCanvasSearch] = useState('');
  const [previewLanguage, setPreviewLanguage] = useState('');
  const [canvasLocalization, setCanvasLocalization] = useState(null);
  const [hoverBranch, setHoverBranch] = useState(null);
  useEffect(() => {
    let active = true;
    setCanvasLocalization(null);
    setPreviewLanguage('');
    if (selectedBot) axios.get('/api/v1/localizations', { params: { botId: selectedBot, env: selectedEnv } })
      .then(({ data }) => { if (active) setCanvasLocalization(data.localization || null); })
      .catch(() => {});
    return () => { active = false; };
  }, [selectedBot, selectedEnv]);
  const reactFlowInstanceRef = useRef(null);
  const debugReplayTimerRef = useRef(null);
  const lastFittedFlowSessionRef = useRef(null);
  const [reactFlowReady, setReactFlowReady] = useState(false);
  const [environmentLoading, setEnvironmentLoading] = useState(false);
  const [environmentName, setEnvironmentName] = useState('');
  const [environmentConfig, setEnvironmentConfig] = useState({});
  const [customTables, setCustomTables] = useState([]);
  const [savedApis, setSavedApis] = useState([]);
  const [savedFunctions, setSavedFunctions] = useState([]);
  const [availableFlows, setAvailableFlows] = useState([]);
  const [panelTab, setPanelTab] = useState('inspector');
  const [dynamicChatFullscreen, setDynamicChatFullscreen] = useState(false);
  const [dynamicChatSettingsOpen, setDynamicChatSettingsOpen] = useState(false);
  const [flowMetaDialogOpen, setFlowMetaDialogOpen] = useState(false);
  const [flowMetaDraft, setFlowMetaDraft] = useState({ name: '', description: '' });
  const [debugSessionId, setDebugSessionId] = useState('');
  const [debugInput, setDebugInput] = useState('hello');
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugPlaying, setDebugPlaying] = useState(false);
  const [debugSteps, setDebugSteps] = useState([]);
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugHistory, setDebugHistory] = useState([]);
  const [debugQuickReplies, setDebugQuickReplies] = useState([]);
  const [debugInputControl, setDebugInputControl] = useState(null);
  const [debugVariables, setDebugVariables] = useState({});
  const [debugTraceId, setDebugTraceId] = useState('');
  const [debugError, setDebugError] = useState('');
  const [debugCurrentStepIndex, setDebugCurrentStepIndex] = useState(-1);
  const [debugCurrentNodeId, setDebugCurrentNodeId] = useState('');
  const debugLanguageRef = useRef(null);
  const [debugLogHeight, setDebugLogHeight] = useState(520);
  const [debugLogsVisible, setDebugLogsVisible] = useState(false);
  const [debugLogsRefreshing, setDebugLogsRefreshing] = useState(false);
  const [debugCanvasRevision, setDebugCanvasRevision] = useState(0);
  const [nodePickerOpen, setNodePickerOpen] = useState(false);
  const [nodePickerCategory, setNodePickerCategory] = useState('prompts');
  const [nodePickerSearch, setNodePickerSearch] = useState('');
  const [nodePickerSourceId, setNodePickerSourceId] = useState('');
  const [nodePickerSourceHandle, setNodePickerSourceHandle] = useState('');

  const {
    nodes,
    edges,
    flowName,
    flowDescription,
    variables,
    selectedNodeId,
    environmentId,
    inspectorCollapsed,
    flowId: storeFlowId,
    loadingFlow,
    savingFlow,
    error,
    notice,
    recentNodeKeys,
    editorLoadRevision,
    setFlowData,
    setVariables,
    setNodes,
    setEdges,
    setSelectedNodeId,
    clearSelection,
    setInspectorCollapsed,
    setLoadingFlow,
    setSavingFlow,
    setError,
    setNotice,
    setHeaderActions,
    clearHeaderActions,
    hydrateFromApi,
    resetToStarter,
    updateNodeData,
    reorderQuickReplyOptions,
    deleteNode,
    duplicateNode,
    updateEdge,
    removeEdge,
    addNode,
    setFlowAfterSave,
    getSavePayload,
    markNodeAsRecentlyUsed,
    addVariable,
    updateVariable,
    removeVariable,
  } = useFlowBuilderStore();
  const [selectedEdgeId, setSelectedEdgeId] = useState('');
  const [analyticsMode, setAnalyticsMode] = useState(false);
  const clipboardNodesRef = useRef([]);
  const duplicateNodeRef = useRef(null);
  const runFromHereRef = useRef(null);

  /** Purpose: Calculate node execution metrics when analytics is enabled. Why: Avoid running analytics aggregation during ordinary editing. */
  const heatmapMetrics = useMemo(() => {
    return analyticsMode ? computeHeatmapMetrics(nodes, edges) : null;
  }, [analyticsMode, nodes, edges]);

  /** Purpose: Resolve the selected node from the current graph. Why: Inspector content must reflect live edits rather than an old node snapshot. */
  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );
  const selectedNodeIsDynamicChat = selectedNode?.data?.libraryKey === 'dynamic_chat'
    || selectedNode?.type === 'genai_prompt'
    || selectedNode?.data?.config?.uiNodeKey === 'dynamic_chat';
  useEffect(() => {
    setDynamicChatFullscreen(Boolean(selectedNodeIsDynamicChat));
    setDynamicChatSettingsOpen(false);
  }, [selectedNodeId, selectedNodeIsDynamicChat]);

  /** Purpose: Resolve the selected connection from current edges. Why: Connection editing must track replacements and deletions. */
  const selectedEdge = useMemo(
    () => edges.find((edge) => edge.id === selectedEdgeId) || null,
    [edges, selectedEdgeId]
  );

  /** Purpose: Collect node IDs visited by the debug trace. Why: A set makes repeated canvas highlighting checks inexpensive. */
  const debugVisitedNodeIds = useMemo(() => {
    return new Set(debugSteps.map((step) => step?.nodeId).filter(Boolean));
  }, [debugSteps]);

  /** Purpose: Resolve the current debugger step. Why: A negative index represents no selected execution. */
  const selectedDebugStep = useMemo(
    () => (debugCurrentStepIndex >= 0 ? debugSteps[debugCurrentStepIndex] || null : null),
    [debugCurrentStepIndex, debugSteps]
  );

  useEffect(() => {
    if (queryNodeId && nodes.length > 0) {
      /** Purpose: Locate the graph node referenced by the requested ID. Why: Navigation must reconcile the route query with stored node identity. */
      const target = nodes.find((n) => n.id === queryNodeId || n.nodeId === queryNodeId);
      if (target) {
        setSelectedNodeId(target.id);
        setPanelTab('inspector');
        if (reactFlowInstanceRef.current) {
          reactFlowInstanceRef.current.fitView({
            nodes: [{ id: target.id }],
            duration: 400,
            maxZoom: 1.1,
          });
        }
      }
    }
  }, [queryNodeId, nodes, setSelectedNodeId]);

  /** Purpose: Decorate stored nodes with canvas state and interaction handlers. Why: Presentation callbacks and debugger highlights should not become saved flow configuration. */
  const displayNodes = useMemo(() => {
    /** Purpose: Index quick-reply connections by destination. Why: Target cards need the originating answer labels without changing execution data. */
    const incomingQuickReplyRoutes = edges.reduce((routesByTarget, edge) => {
      /** Purpose: Resolve the source of an incoming connection. Why: Only sources with quick-reply options contribute answer labels. */
      const sourceNode = nodes.find((node) => node.id === edge.source);
      const option = getQuickReplyOptionForHandle(sourceNode, edge.sourceHandle)
        || (isQuickReplySourceNode(sourceNode) ? String(edge.data?.condition || edge.label || '').trim() : '');
      if (!option) return routesByTarget;

      const routes = routesByTarget.get(edge.target) || [];
      routes.push({
        option,
        sourceTitle: sourceNode?.data?.title || 'Quick replies',
      });
      routesByTarget.set(edge.target, routes);
      return routesByTarget;
    }, new Map());

    return nodes.map((node) => {
      const targetFlowId = String(node.data?.config?.targetFlowId || node.data?.config?.jumpToFlowId || '').trim();
      const matchedFlow = targetFlowId
        ? availableFlows.find((f) => f.flowId === targetFlowId || String(f._id) === targetFlowId || f.name === targetFlowId)
        : null;

      return {
        ...node,
        style: { ...node.style, opacity: hoverBranch && node.id !== hoverBranch.source && node.id !== hoverBranch.target ? 0.3 : 1 },
        data: {
          ...node.data,
          config: previewLanguage ? previewConfig(node.data?.config, canvasLocalization, previewLanguage) : node.data?.config,
          previewKey: node.data?.config?.localizationKey,
          editorWarnings: editorWarnings(node, edges, canvasLocalization, savedFunctions, availableFlows),
          targetFlowName: matchedFlow?.name || null,
          targetFlowMissing: Boolean(targetFlowId && !matchedFlow && availableFlows.length > 0),
          /** Purpose: Select this node and reveal its inspector. Why: Card actions must use the same selection state as canvas clicks. */
          onInspect: () => { setSelectedNodeId(node.id); setPanelTab('inspector'); },
          /** Purpose: Invoke the latest duplicate handler for this node. Why: A ref avoids stale closures in memoized node decorations. */
          onDuplicate: (targetId) => duplicateNodeRef.current?.(targetId || node.id),
          /** Purpose: Start a debug run from this node through the current handler. Why: Memoized canvas cards must not capture outdated flow or session state. */
          onRunFromHere: (targetId) => runFromHereRef.current?.(targetId || node.id),
          /** Purpose: Delete the requested node through the current handler. Why: Card deletion must share the editor's graph cleanup behavior. */
          onDelete: (targetId) => handleDeleteNode(targetId || node.id),
          isCurrent: node.id === debugCurrentNodeId,
          isVisited: debugVisitedNodeIds.has(node.id),
          /** Purpose: Open the node picker for this source and output handle. Why: The next node must connect to the outcome the user chose. */
          onOpenNodePicker: ({ sourceNodeId, sourceHandle = '' }) => {
            setNodePickerSourceId(sourceNodeId || node.id);
            setNodePickerSourceHandle(sourceHandle);
            setNodePickerOpen(true);
          },
          /** Purpose: Track the hovered branch. Why: The canvas can emphasize the matching connection without mutating it. */
          onBranchHover: (handle) => setHoverBranch(handle ? edges.find((edge) => edge.source === node.id && edge.sourceHandle === handle) || null : null),
          /** Purpose: Navigate to the target of this output branch. Why: Branch controls provide direct access to the next step on large canvases. */
          onGoToBranch: (handle) => {
            /** Purpose: Find the edge for the chosen source handle. Why: Each branch navigation action must follow its own outcome. */
            const edge = edges.find((item) => item.source === node.id && item.sourceHandle === handle);
            if (edge) {
              setSelectedNodeId(edge.target);
              reactFlowInstanceRef.current?.fitView({ nodes: [{ id: edge.target }], duration: 300, maxZoom: 1.1 });
            }
          },
          nodeId: node.id,
          connectedSourceHandles: edges
            .filter((edge) => edge.source === node.id)
            .map((edge) => String(edge.sourceHandle || 'next')),
          incomingQuickReplyRoutes: incomingQuickReplyRoutes.get(node.id) || [],
          quickReplyFallbackConfigured: edges.some((edge) => edge.source === node.id && String(edge.data?.condition || edge.label || '').trim().toLowerCase() === 'fallback'),
          analytics: heatmapMetrics ? heatmapMetrics.nodeMetrics.get(node.id) : null,
          showAnalytics: analyticsMode,
        },
      };
    });
  }, [nodes, edges, debugCurrentNodeId, debugVisitedNodeIds, hoverBranch, previewLanguage, canvasLocalization, savedFunctions, availableFlows, setSelectedNodeId, heatmapMetrics, analyticsMode]);

  // Older flows may have saved every quick-reply branch with the same source
  // handle. The button label is the source of truth, so repair those edges in
  // state as soon as the flow is loaded. This also makes the correction persist
  // on the next normal flow save.
  useEffect(() => {
    let changed = false;
    /** Purpose: Normalize API and handoff outcome handles for display. Why: Legacy connection data must map to the current node ports. */
    const normalizedEdges = edges.map((edge) => {
      /** Purpose: Resolve the source node for edge normalization. Why: Branch rules depend on the source node's type. */
      const sourceNode = nodes.find((node) => node.id === edge.source);
      const apiBranch = getApiEdgeBranch(sourceNode, edge);
      const handoffBranch = getHandoffEdgeBranch(sourceNode, edge, edges);
      if (apiBranch && (
        edge.sourceHandle !== apiBranch.sourceHandle
        || edge.data?.condition !== apiBranch.condition
        || edge.label !== apiBranch.label
      )) {
        changed = true;
        return {
          ...edge,
          sourceHandle: apiBranch.sourceHandle,
          label: apiBranch.label,
          data: { ...(edge.data || {}), condition: apiBranch.condition },
          markerEnd: { ...(edge.markerEnd || {}), type: MarkerType.ArrowClosed, color: apiBranch.color },
          style: { ...(edge.style || {}), stroke: apiBranch.color, strokeWidth: 2 },
        };
      }
      if (handoffBranch && (
        edge.sourceHandle !== handoffBranch.sourceHandle
        || edge.data?.condition !== handoffBranch.condition
        || edge.label !== handoffBranch.label
      )) {
        changed = true;
        return {
          ...edge,
          sourceHandle: handoffBranch.sourceHandle,
          label: handoffBranch.label,
          data: { ...(edge.data || {}), condition: handoffBranch.condition },
        };
      }
      const sourceHandle = getQuickReplySourceHandle(sourceNode, edge);
      if (sourceHandle && sourceHandle !== edge.sourceHandle) {
        changed = true;
        return { ...edge, sourceHandle };
      }
      return edge;
    });

    if (changed) setEdges(normalizedEdges);
  }, [edges, nodes, setEdges]);

  /** Purpose: Decorate connections with labels, styles, and interaction state. Why: Canvas presentation stays separate from persisted routing. */
  const displayEdges = useMemo(() => {
    return edges.map((edge) => {
      const isSelected = edge.id === selectedEdgeId;
      /** Purpose: Resolve an edge's source for branch-specific styling. Why: Different node types expose different outcome semantics. */
      const sourceNode = nodes.find((node) => node.id === edge.source);
      const isAbSplitSource = sourceNode?.data?.libraryKey === 'ab_split' || sourceNode?.data?.config?.uiNodeKey === 'ab_split';
      const abBranch = edge.sourceHandle === 'path_b' || edge.data?.condition === 'path_b' ? 'path_b' : 'path_a';
      const abColor = isAbSplitSource ? (abBranch === 'path_a' ? '#38bdf8' : '#ec4899') : null;
      const abLabel = isAbSplitSource ? (abBranch === 'path_a' ? 'Path A' : 'Path B') : null;
      const apiBranch = getApiEdgeBranch(sourceNode, edge);
      const handoffBranch = getHandoffEdgeBranch(sourceNode, edge, edges);
      const normalizedSourceHandle = isAbSplitSource ? abBranch : (apiBranch?.sourceHandle || handoffBranch?.sourceHandle || getQuickReplySourceHandle(sourceNode, edge));
      const quickReplyLabel = getQuickReplyOptionForHandle(sourceNode, normalizedSourceHandle)
        || (isQuickReplySourceNode(sourceNode) ? String(edge.data?.condition || edge.label || '').trim() : '');
      const quickReplyColors = ['#8b5cf6', '#0ea5e9', '#f59e0b', '#14b8a6', '#ec4899'];
      /** Purpose: Find the answer represented by this quick-reply edge. Why: Stable option matching determines its display treatment. */
      const quickReplyIndex = getQuickReplyOptions(sourceNode).findIndex((option) => option.toLowerCase() === quickReplyLabel.toLowerCase());
      const quickReplyColor = quickReplyLabel.toLowerCase() === 'fallback'
        ? '#f43f5e'
        : quickReplyIndex >= 0 ? quickReplyColors[quickReplyIndex % quickReplyColors.length] : null;
      const isActive = Boolean(
        debugCurrentNodeId &&
        (edge.target === debugCurrentNodeId || edge.source === debugCurrentNodeId)
      );

      return {
        ...edge,
        type: 'premium',
        sourceHandle: normalizedSourceHandle,
        animated: isActive || edge.animated,
        label: abLabel || apiBranch?.label || handoffBranch?.label || quickReplyLabel || edge.label,
        labelStyle: {
          fill: isSelected ? '#f8fafc' : (abColor || quickReplyColor || '#334155'),
          fontWeight: isSelected || quickReplyColor || abColor ? 800 : 600,
          fontSize: quickReplyColor || abColor ? 11 : 10,
        },
        labelBgStyle: {
          fill: isSelected ? '#0f172a' : '#ffffff',
          fillOpacity: isSelected ? 0.95 : 0.9,
        },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 6,
        style: {
          ...(edge.style || {}),
          opacity: hoverBranch && edge.id !== hoverBranch.id ? 0.2 : 1,
          stroke: isSelected ? '#f43f5e' : (isActive ? '#f59e0b' : abColor || apiBranch?.color || quickReplyColor || edge.style?.stroke || '#67e8f9'),
          strokeWidth: isSelected ? 3.5 : (isActive ? 3 : edge.style?.strokeWidth || 2),
        },
        markerEnd: {
          ...(edge.markerEnd || {}),
          type: MarkerType.ArrowClosed,
          color: abColor || apiBranch?.color || edge.markerEnd?.color || edge.style?.stroke || '#67e8f9',
        },
        data: {
          ...(edge.data || {}),
          color: isSelected ? '#f43f5e' : (isActive ? '#f59e0b' : abColor || apiBranch?.color || quickReplyColor || edge.style?.stroke || '#67e8f9'),
          isActive,
          analytics: heatmapMetrics ? heatmapMetrics.edgeMetrics.get(edge.id) : null,
        },
      };
    });
  }, [edges, nodes, debugCurrentNodeId, selectedEdgeId, hoverBranch, heatmapMetrics]);

  const botDisplayName = selectedBotDetails?.botName || selectedBot || 'Bot Preview';
  const botLabel = selectedBot ? `${selectedBot}${selectedEnv ? ` • ${selectedEnv}` : ''}` : 'No bot selected';
  const isNewFlow = !flowId || flowId === 'new';
  const activeFlowId = storeFlowId || (flowId && flowId !== 'new' ? flowId : '');
  /** Purpose: Locate the graph entry node. Why: New-node placement and flow controls need a consistent entry reference. */
  const startNode = useMemo(() => nodes.find((node) => node.type === 'start') || null, [nodes]);
  /** Purpose: Resolve recent node keys against the catalog. Why: Removed or unavailable templates should not appear as selectable items. */
  const recentNodeItems = useMemo(
    () => recentNodeKeys.map((key) => findFlowNodeItem(key)).filter(Boolean),
    [recentNodeKeys]
  );

  /** Purpose: Load the selected bot environment and its variables. Why: Editor configuration must stay scoped to the active environment. */
  const loadEnvironment = useCallback(async () => {
    if (!selectedBot || !selectedEnv) {
      return null;
    }

    setEnvironmentLoading(true);
    try {
      const response = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
      const environment = response.data?.environment || null;
      setEnvironmentConfig(environment?.config?.chatWidget || {});
      setEnvironmentName(environment?.name || selectedEnv || '');
      return environment;
    } catch (loadError) {
      setEnvironmentConfig({});
      setEnvironmentName(selectedEnv || '');
      return null;
    } finally {
      setEnvironmentLoading(false);
    }
  }, [selectedBot, selectedEnv]);

  /** Purpose: Load custom tables available in the current bot environment. Why: Data-node options must not mix resources across environments. */
  const loadCustomTables = useCallback(async (resolvedEnvironmentId = '') => {
    if (!selectedBot || !resolvedEnvironmentId) {
      setCustomTables([]);
      return;
    }

    try {
      const response = await axios.get(`/api/v1/custom-data/tables?botId=${selectedBot}&environmentId=${resolvedEnvironmentId}`);
      setCustomTables(Array.isArray(response.data?.tables) ? response.data.tables : []);
    } catch (error) {
      setCustomTables([]);
    }
  }, [selectedBot]);

  /** Purpose: Load reusable API definitions. Why: API nodes reference centrally managed definitions rather than duplicate their setup. */
  const loadSavedApis = useCallback(async (resolvedEnvironmentId = '') => {
    if (!selectedBot || !resolvedEnvironmentId) {
      setSavedApis([]);
      return;
    }

    try {
      const response = await axios.get(`/api/v1/api-definitions?botId=${selectedBot}&environmentId=${resolvedEnvironmentId}`);
      setSavedApis(Array.isArray(response.data?.apis) ? response.data.apis : []);
    } catch (error) {
      setSavedApis([]);
    }
  }, [selectedBot]);

  /** Purpose: Load reusable function definitions. Why: Function selectors need the workspace's current callable resources. */
  const loadSavedFunctions = useCallback(async (resolvedEnvironmentId = '') => {
    if (!selectedBot || !resolvedEnvironmentId) {
      setSavedFunctions([]);
      return;
    }
    try {
      const response = await axios.get(`/api/v1/function-definitions?botId=${selectedBot}&environmentId=${resolvedEnvironmentId}`);
      setSavedFunctions(Array.isArray(response.data?.functions) ? response.data.functions : []);
    } catch (error) {
      setSavedFunctions([]);
    }
  }, [selectedBot]);

  /** Purpose: Initialize the graph and supporting editor catalogs. Why: Authentication and bot/environment selection must be ready before scoped data is loaded. */
  const initBuilder = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (!selectedBot || !selectedEnv) {
      setError('Select a bot and environment to load the builder.');
      resetToStarter();
      setFlowData({ flowId: '', botId: '', environmentId: '' });
      return;
    }

    setError('');
    setLoadingFlow(true);

    try {
      const environment = await loadEnvironment();
      const resolvedEnvironmentId = environment?.envId || environment?.environmentId || environmentId || '';
      const flowsResponse = await axios.get('/api/v1/flows', { params: { botId: selectedBot, environmentId: resolvedEnvironmentId } });
      setAvailableFlows(Array.isArray(flowsResponse.data?.flows) ? flowsResponse.data.flows : []);
      await loadCustomTables(resolvedEnvironmentId);
      await loadSavedApis(resolvedEnvironmentId);
      await loadSavedFunctions(resolvedEnvironmentId);

      if (isNewFlow) {
        const starter = createStarterGraph();
        resetToStarter();
        setFlowData({
          flowId: '',
          flowName: starter.flowName,
          flowDescription: starter.flowDescription,
          botId: selectedBot,
          environmentId: resolvedEnvironmentId,
          entryNodeId: starter.entryNodeId,
        });
        setSelectedNodeId(starter.entryNodeId);
        if (reactFlowInstanceRef.current) {
          setTimeout(() => {
            reactFlowInstanceRef.current.fitView({ padding: 0.2, duration: 250 });
          }, 0);
        }
        return;
      }

      const response = await axios.get(`/api/v1/flows/${flowId}`, {
        params: { botId: selectedBot, environmentId: resolvedEnvironmentId },
      });
      const flow = response.data?.flow;
      if (!flow) {
        throw new Error('Flow not found');
      }

      hydrateFromApi(flow);
      setFlowData({
        flowId: flow.flowId || flowId,
        flowName: flow.name || 'Untitled flow',
        flowDescription: flow.description || '',
        botId: flow.botId || selectedBot,
        environmentId: flow.environmentId || resolvedEnvironmentId,
        entryNodeId: flow.entryNodeId || '',
      });
      if (reactFlowInstanceRef.current) {
        setTimeout(() => {
          reactFlowInstanceRef.current.fitView({ padding: 0.2, duration: 250 });
        }, 0);
      }
    } catch (loadError) {
      console.error('Failed to load builder flow:', loadError);
      setError(loadError.response?.data?.error || loadError.message || 'Failed to load flow');
      const starter = createStarterGraph();
      resetToStarter();
      setFlowData({
        flowId: '',
        flowName: starter.flowName,
        flowDescription: starter.flowDescription,
        botId: selectedBot || '',
        environmentId: environmentId || '',
        entryNodeId: starter.entryNodeId,
      });
    } finally {
      setLoadingFlow(false);
    }
  }, [
    authLoading,
    selectedBot,
    selectedEnv,
    isNewFlow,
    flowId,
    environmentId,
    loadEnvironment,
    loadCustomTables,
    loadSavedApis,
    loadSavedFunctions,
    setError,
    resetToStarter,
    setFlowData,
    setSelectedNodeId,
    hydrateFromApi,
    setLoadingFlow,
  ]);

  /** Purpose: Clear debugger state for a fresh run. Why: Traces, variables, and playback from an earlier conversation must not leak into the next test. */
  const resetDebugger = useCallback(() => {
    setDebugSteps([]);
    setDebugLogs([]);
    setDebugHistory([]);
    setDebugQuickReplies([]);
    setDebugInputControl(null);
    setDebugVariables({});
    setDebugTraceId('');
    setDebugError('');
    setDebugCurrentStepIndex(-1);
    setDebugCurrentNodeId('');
    setDebugPlaying(false);
    setDebugInput('hello');
    setDebugSessionId(`debug-${Date.now()}`);
    if (debugReplayTimerRef.current) {
      clearInterval(debugReplayTimerRef.current);
      debugReplayTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    initBuilder();
  }, [initBuilder]);

  useEffect(() => {
    resetDebugger();
  }, [selectedBot, selectedEnv, flowId, resetDebugger]);

  useEffect(() => {
    if (!notice && !error) return;
    /** Purpose: Clear transient feedback after its display interval. Why: Old notices should not obscure later editing results. */
    const timeout = setTimeout(() => {
      setNotice('');
      if (error) {
        setError('');
      }
    }, 3500);
    return () => clearTimeout(timeout);
  }, [notice, error, setNotice, setError]);

  useEffect(() => {
    if (!debugPlaying || debugSteps.length === 0) {
      if (debugReplayTimerRef.current) {
        clearInterval(debugReplayTimerRef.current);
        debugReplayTimerRef.current = null;
      }
      return undefined;
    }

    debugReplayTimerRef.current = setInterval(() => {
      setDebugCurrentStepIndex((current) => {
        const nextIndex = current < 0 ? 0 : Math.min(current + 1, debugSteps.length - 1);
        const nextStep = debugSteps[nextIndex];
        if (nextStep?.nodeId) {
          setDebugCurrentNodeId(nextStep.nodeId);
        }
        if (nextIndex >= debugSteps.length - 1) {
          setDebugPlaying(false);
        }
        return nextIndex;
      });
    }, 900);

    return () => {
      if (debugReplayTimerRef.current) {
        clearInterval(debugReplayTimerRef.current);
        debugReplayTimerRef.current = null;
      }
    };
  }, [debugPlaying, debugSteps]);

  useEffect(() => {
    const currentStep = debugSteps[debugCurrentStepIndex];
    if (currentStep?.nodeId) {
      setDebugCurrentNodeId(currentStep.nodeId);
    }
  }, [debugCurrentStepIndex, debugSteps]);

  useEffect(() => {
    if (!reactFlowReady || loadingFlow || nodes.length === 0) {
      return;
    }

    const flowSessionKey = `${selectedBot}_${selectedEnv}_${activeFlowId || 'new'}_${editorLoadRevision}`;
    if (lastFittedFlowSessionRef.current === flowSessionKey) {
      return;
    }

    lastFittedFlowSessionRef.current = flowSessionKey;
    /** Purpose: Fit the initial graph after rendering. Why: Canvas dimensions must settle before calculating a useful viewport. */
    const timer = setTimeout(() => {
      reactFlowInstanceRef.current?.fitView({
        padding: 0.22,
        duration: 320,
        minZoom: 0.45,
        maxZoom: 1.15,
      });
    }, 80);

    return () => clearTimeout(timer);
  }, [reactFlowReady, loadingFlow, selectedBot, selectedEnv, activeFlowId, editorLoadRevision, nodes.length === 0]);

  /** Purpose: Apply React Flow node changes to the latest store state. Why: Functional updates preserve concurrent position and selection changes. */
  const handleNodeChanges = useCallback(
    (changes) => {
      setNodes((currentNodes) => applyNodeChanges(changes, currentNodes));
    },
    [setNodes]
  );

  /** Purpose: Apply React Flow edge changes to the latest store state. Why: Connection changes must not overwrite newer graph edits. */
  const handleEdgeChanges = useCallback(
    (changes) => {
      setEdges((currentEdges) => applyEdgeChanges(changes, currentEdges));
    },
    [setEdges]
  );

  /** Purpose: Reject incomplete, self, and invalid terminal connections. Why: The canvas should prevent structurally invalid routes before saving. */
  const isValidConnection = useCallback(
    (connection) => {
      if (!connection.source || !connection.target || connection.source === connection.target) {
        return false;
      }

      /** Purpose: Resolve the proposed connection's source. Why: Terminal nodes cannot expose outgoing routes. */
      const sourceNode = nodes.find((node) => node.id === connection.source);
      return sourceNode?.type !== 'end';
    },
    [nodes]
  );

  /** Purpose: Create a normalized connection and replace an existing route for the same outcome. Why: Each outcome has one destination; reading current store edges avoids stale-state duplicate branches. */
  const handleConnect = useCallback(
    (params) => {
      if (!params.source || !params.target) return;
      /** Purpose: Resolve the source type for connection creation. Why: Outcome names and handles must follow that node's routing contract. */
      const sourceNode = nodes.find((node) => node.id === params.source);
      if (!sourceNode || sourceNode.type === 'end' || params.source === params.target) return;

      const isApiSourceNode = (sourceNode.type === 'api_call' && sourceNode?.data?.libraryKey !== 'function') || sourceNode?.data?.libraryKey === 'dynamic_chat';
      const isChatHandoffSourceNode = sourceNode?.data?.libraryKey === 'chat_handoff';
      const isAbSplitSourceNode = sourceNode?.data?.libraryKey === 'ab_split' || sourceNode?.data?.config?.uiNodeKey === 'ab_split';
      const allowsMultipleNextSteps = sourceNode.type === 'condition' || isQuickReplySourceNode(sourceNode) || isApiSourceNode || isChatHandoffSourceNode || isAbSplitSourceNode;
      const selectedQuickReplyOption = getQuickReplyOptionForHandle(sourceNode, params.sourceHandle);
      const replaceBranchEdge = isApiSourceNode || isChatHandoffSourceNode || isAbSplitSourceNode || (isQuickReplySourceNode(sourceNode) && (selectedQuickReplyOption || isQuickReplyFallbackHandle(params.sourceHandle)));
      const selectedBranchCondition = isAbSplitSourceNode
        ? (params.sourceHandle === 'path_b' ? 'path_b' : 'path_a')
        : isApiSourceNode
          ? (getApiEdgeBranch(sourceNode, { sourceHandle: params.sourceHandle })?.condition || 'success')
          : isChatHandoffSourceNode
            ? (params.sourceHandle === 'handoff-timeout' ? 'handoff_timeout' : 'resolved')
            : String(selectedQuickReplyOption || '').toLowerCase();
      setEdges((currentEdges) => {
        const nextEdges = replaceBranchEdge
          ? currentEdges.filter((edge) => (
            edge.source !== params.source
            || (edge.sourceHandle !== params.sourceHandle
              && String(edge.data?.condition || edge.label || '').trim().toLowerCase() !== selectedBranchCondition)
          ))
          : allowsMultipleNextSteps
            ? currentEdges
            : currentEdges.filter((edge) => edge.source !== params.source);

        if (nextEdges.some((edge) => (
          edge.source === params.source
          && edge.target === params.target
          && (edge.sourceHandle || 'next') === (params.sourceHandle || 'next')
        ))) return currentEdges;

        const nextEdge = buildEdge({
          source: params.source,
          target: params.target,
          sourceHandle: params.sourceHandle,
          sourceNode,
          edges: nextEdges,
        });
        return [...nextEdges, nextEdge];
      });
      setSelectedEdgeId('');
    },
    [nodes, setEdges]
  );

  /** Purpose: Replace a moved connection while removing conflicting routes for its outcome. Why: Reconnection must preserve edge identity without leaving duplicate success or failure branches. */
  const handleEdgeReconnect = useCallback((oldEdge, connection) => {
    if (!connection.source || !connection.target) return;
    /** Purpose: Resolve the source of the moved connection. Why: Reconnecting to another source requires that source's outcome rules. */
    const sourceNode = nodes.find((node) => node.id === connection.source);
    if (!sourceNode) return;
    setEdges((currentEdges) => {
      /** Purpose: Exclude the connection being moved. Why: The replacement must not coexist with its previous endpoint. */
      const withoutOldEdge = currentEdges.filter((edge) => edge.id !== oldEdge.id);
      const isBranchNode = (sourceNode.type === 'api_call' && sourceNode?.data?.libraryKey !== 'function') || sourceNode?.data?.libraryKey === 'dynamic_chat';
      const selectedBranchCondition = isBranchNode
        ? getApiEdgeBranch(sourceNode, { sourceHandle: connection.sourceHandle })?.condition || 'success'
        : '';
      const baseEdges = isBranchNode
        ? withoutOldEdge.filter((edge) => edge.source !== connection.source || (
          edge.sourceHandle !== connection.sourceHandle
          && String(edge.data?.condition || edge.label || '').trim().toLowerCase() !== selectedBranchCondition
        ))
        : withoutOldEdge;
      const replacement = buildEdge({
        source: connection.source,
        target: connection.target,
        sourceHandle: connection.sourceHandle,
        sourceNode,
        edges: baseEdges,
      });
      return [...baseEdges, {
        ...oldEdge,
        source: connection.source,
        target: connection.target,
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
        label: replacement.label,
        data: replacement.data,
      }];
    });
    setSelectedEdgeId(oldEdge.id);
  }, [nodes, setEdges]);

  /** Purpose: Create a node from a dropped catalog payload. Why: Screen coordinates must be translated into flow coordinates before placement. */
  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      const nodeType = safeParseJson(event.dataTransfer.getData('application/reactflow'))
        || event.dataTransfer.getData('application/reactflow');

      if (!nodeType || !reactFlowInstanceRef.current) {
        return;
      }

      const bounds = event.currentTarget.getBoundingClientRect();
      const position = reactFlowInstanceRef.current.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const item = typeof nodeType === 'string' ? findFlowNodeItem(nodeType) : nodeType;
      const uiType = item?.type || nodeType?.type || nodeType;
      const node = addNode(uiType, position, {
        libraryKey: item?.key || '',
        template: item || null,
      });
      setTimeout(() => {
        setSelectedNodeId(node.id);
      }, 0);
    },
    [addNode, setSelectedNodeId]
  );

  /** Purpose: Allow catalog items to be dropped on the canvas. Why: The browser otherwise treats the canvas as an invalid drop target. */
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  /** Purpose: Serialize the node template into drag data. Why: The drop handler needs a portable description of the requested node. */
  const handleSidebarDragStart = useCallback((event, item) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(item));
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  /** Purpose: Select a node and open its inspector. Why: Node and edge selection must remain mutually exclusive. */
  const handleNodeClick = useCallback(
    (_event, node) => {
      setSelectedEdgeId('');
      setSelectedNodeId(node.id);
      setPanelTab('inspector');
    },
    [setSelectedNodeId]
  );

  /** Purpose: Clear selection when the empty canvas is clicked. Why: Inspector state should not imply that an item remains selected. */
  const handlePaneClick = useCallback(() => {
    setSelectedEdgeId('');
    clearSelection();
  }, [clearSelection]);

  /** Purpose: Select a connection and open its inspector. Why: Route editing requires edge selection rather than node selection. */
  const handleEdgeClick = useCallback((_event, edge) => {
    clearSelection();
    setSelectedEdgeId(edge.id);
    setPanelTab('inspector');
  }, [clearSelection]);

  /** Purpose: Apply inspector configuration changes through the graph store. Why: One update path keeps node data and editor history consistent. */
  const handleNodeUpdate = useCallback(
    (nodeId, patch) => {
      updateNodeData(nodeId, patch);
    },
    [updateNodeData]
  );

  /** Purpose: Delete a node through the graph store. Why: The store owns cleanup of dependent connections. */
  const handleDeleteNode = useCallback(
    (nodeId) => {
      deleteNode(nodeId);
    },
    [deleteNode]
  );

  /** Purpose: Apply edits to a connection through the store. Why: Routing changes must participate in the same state lifecycle as graph edits. */
  const handleEdgeUpdate = useCallback((edgeId, patch) => {
    updateEdge(edgeId, patch);
  }, [updateEdge]);

  /** Purpose: Remove a connection and clear its selection. Why: The inspector must not keep editing a deleted edge. */
  const handleDeleteEdge = useCallback((edgeId) => {
    removeEdge(edgeId);
    setSelectedEdgeId((current) => (current === edgeId ? '' : current));
  }, [removeEdge]);

  /** Purpose: Center the viewport on a node. Why: New or duplicated steps need to be discoverable on large canvases. */
  const focusNodeInCanvas = useCallback((node) => {
    if (!node || !reactFlowInstanceRef.current) return;
    const x = (node.position?.x || 0) + 180;
    const y = (node.position?.y || 0) + 120;
    reactFlowInstanceRef.current.setCenter(x, y, { zoom: 1, duration: 360 });
  }, []);

  /** Purpose: Duplicate the selected step and focus its copy. Why: The copy should be immediately available for editing without losing the original. */
  const handleDuplicateNode = useCallback(
    (nodeId) => {
      const targetId = nodeId || selectedNodeId;
      if (!targetId) return;
      const newNode = duplicateNode(targetId);
      if (newNode) {
        setNotice('Node duplicated.');
        setTimeout(() => focusNodeInCanvas(newNode), 80);
      }
    },
    [duplicateNode, selectedNodeId, focusNodeInCanvas, setNotice]
  );
  duplicateNodeRef.current = handleDuplicateNode;

  /** Purpose: Arrange nodes into topology-based columns and rows. Why: Automatic layout improves readability while preserving graph connectivity. */
  const handleTidyUpLayout = useCallback(() => {
    if (!nodes || nodes.length === 0) return;

    const start = nodes.find((n) => n.type === 'start') || nodes[0];
    const outgoing = new Map();
    const incomingCount = new Map();
    nodes.forEach((n) => {
      outgoing.set(n.id, []);
      incomingCount.set(n.id, 0);
    });

    edges.forEach((edge) => {
      if (outgoing.has(edge.source) && outgoing.has(edge.target)) {
        outgoing.get(edge.source).push(edge.target);
        incomingCount.set(edge.target, (incomingCount.get(edge.target) || 0) + 1);
      }
    });

    const levels = new Map();
    const visited = new Set();
    const queue = [{ id: start.id, level: 0 }];
    visited.add(start.id);
    levels.set(start.id, 0);

    while (queue.length > 0) {
      const { id, level } = queue.shift();
      const nextLevel = level + 1;
      const neighbors = outgoing.get(id) || [];
      for (const targetId of neighbors) {
        const currentLevel = levels.get(targetId) || 0;
        if (nextLevel > currentLevel) {
          levels.set(targetId, nextLevel);
        }
        if (!visited.has(targetId)) {
          visited.add(targetId);
          queue.push({ id: targetId, level: nextLevel });
        }
      }
    }

    let maxAssignedLevel = 0;
    levels.forEach((lvl) => {
      if (lvl > maxAssignedLevel) maxAssignedLevel = lvl;
    });

    const orphanLevel = maxAssignedLevel + 1;
    nodes.forEach((n) => {
      if (!levels.has(n.id)) {
        levels.set(n.id, orphanLevel);
      }
    });

    const columns = new Map();
    nodes.forEach((n) => {
      const lvl = levels.get(n.id) || 0;
      if (!columns.has(lvl)) columns.set(lvl, []);
      columns.get(lvl).push(n);
    });

    const NODE_WIDTH_GAP = 360;
    const NODE_HEIGHT_GAP = 220;
    const START_X = 80;
    const START_Y = 120;

    /** Purpose: Assign layout positions from computed columns and row order. Why: Only presentation coordinates should change during tidy-up. */
    const updatedNodes = nodes.map((n) => {
      const col = levels.get(n.id) || 0;
      const colNodes = columns.get(col) || [n];
      /** Purpose: Find this node's row within its column. Why: Nodes at the same graph depth need distinct vertical positions. */
      const indexInCol = colNodes.findIndex((item) => item.id === n.id);
      const yOffset = indexInCol * NODE_HEIGHT_GAP;

      return {
        ...n,
        position: {
          x: Math.round(START_X + col * NODE_WIDTH_GAP),
          y: Math.round(START_Y + yOffset),
        },
      };
    });

    setNodes(updatedNodes);
    setNotice('Flow canvas auto-aligned.');
    setTimeout(() => {
      reactFlowInstanceRef.current?.fitView({ padding: 0.2, duration: 400 });
    }, 60);
  }, [nodes, edges, setNodes, setNotice]);

  /** Purpose: Open the catalog with source and category context. Why: New steps should attach to the user's intended branch. */
  const openNodePicker = useCallback(({ sourceNodeId = '', sourceHandle = '', preferredKey = '' } = {}) => {
    setNodePickerSourceId(sourceNodeId || selectedNodeId || startNode?.id || '');
    setNodePickerSourceHandle(sourceHandle);
    setNodePickerSearch('');
    if (preferredKey) {
      const preferredItem = findFlowNodeItem(preferredKey);
      if (preferredItem?.category) {
        setNodePickerCategory(preferredItem.category);
      }
    }
    setNodePickerOpen(true);
  }, [selectedNodeId, startNode?.id]);

  /** Purpose: Create a chosen template and connect it to the selected source outcome. Why: Node creation must preserve branch identity as well as sensible placement. */
  const handleSelectNodeTemplate = useCallback((item) => {
    const sourceNode = nodes.find((node) => node.id === nodePickerSourceId) || startNode || null;
    const branchOffset = nodePickerSourceHandle === 'failure'
      ? 150
      : nodePickerSourceHandle === 'quick-reply-fallback'
        ? 180
      : String(nodePickerSourceHandle || '').startsWith('quick-reply-')
        ? (Number(String(nodePickerSourceHandle).replace('quick-reply-', '')) + 1) * 130
        : 0;
    const nextPosition = sourceNode
      ? {
          x: nodePickerSourceHandle ? (sourceNode.position?.x || 0) + 380 : (sourceNode.position?.x || 0),
          y: nodePickerSourceHandle ? (sourceNode.position?.y || 0) + branchOffset : (sourceNode.position?.y || 0) + (sourceNode.type === 'start' ? 240 : 220),
        }
      : { x: 220, y: 180 };

    const node = addNode(item.type, nextPosition, {
      libraryKey: item.key,
      template: item,
      title: item.label,
      description: item.description,
      category: item.category,
    });

    if (sourceNode) {
      const supportsBranches = sourceNode.type === 'condition' || isQuickReplySourceNode(sourceNode) || sourceNode.type === 'api_call' || sourceNode?.data?.libraryKey === 'dynamic_chat';
      const retainedEdges = supportsBranches
        ? edges.filter((edge) => edge.source !== sourceNode.id || edge.sourceHandle !== nodePickerSourceHandle)
        : edges.filter((edge) => edge.source !== sourceNode.id);
      const previousEdge = edges.find((edge) => edge.source === sourceNode.id && (!supportsBranches || edge.sourceHandle === nodePickerSourceHandle)) || null;
      const baseEdge = buildEdge({
        source: sourceNode.id,
        target: node.id,
        sourceHandle: nodePickerSourceHandle,
        sourceNode,
        edges: retainedEdges,
      });
      const nextEdge = previousEdge ? {
        ...baseEdge,
        label: previousEdge.label || baseEdge.label,
        data: {
          ...(baseEdge.data || {}),
          ...(previousEdge.data || {}),
        },
      } : baseEdge;

      setEdges([
        ...retainedEdges,
        nextEdge,
      ]);
    }

    markNodeAsRecentlyUsed(item.key);
    setSelectedNodeId(node.id);
    setNodePickerOpen(false);
    setPanelTab('inspector');
    setTimeout(() => focusNodeInCanvas(node), 80);
  }, [nodes, nodePickerSourceId, nodePickerSourceHandle, startNode, addNode, edges, setEdges, markNodeAsRecentlyUsed, setSelectedNodeId, focusNodeInCanvas]);

  useEffect(() => {
    // Purpose: implement editor keyboard shortcuts while respecting text inputs. Why: shortcuts speed up canvas work without stealing keystrokes from configuration fields.
    const handleKeydown = (event) => {
      const target = event.target;
      const isTypingTarget = target instanceof HTMLElement && (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || target.isContentEditable
      );

      if (isTypingTarget) return;

      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedEdgeId) {
        event.preventDefault();
        handleDeleteEdge(selectedEdgeId);
        return;
      }

      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedNodeId && selectedNode?.type !== 'start') {
        event.preventDefault();
        handleDeleteNode(selectedNodeId);
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
        /** Purpose: Collect explicitly selected nodes and the active inspector node. Why: Keyboard operations should work consistently with either selection path. */
        const selectedNodes = nodes.filter((n) => n.selected || n.id === selectedNodeId);
        if (selectedNodes.length > 0) {
          clipboardNodesRef.current = selectedNodes.map((n) => JSON.parse(JSON.stringify(n)));
          setNotice(`Copied ${selectedNodes.length} node${selectedNodes.length > 1 ? 's' : ''}.`);
        }
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
        if (clipboardNodesRef.current && clipboardNodesRef.current.length > 0) {
          event.preventDefault();
          const newNodes = [];
          clipboardNodesRef.current.forEach((n) => {
            if (n.type === 'start') return;
            const newId = `${n.type || 'node'}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
            newNodes.push({
              ...n,
              id: newId,
              position: {
                x: (n.position?.x || 0) + 50,
                y: (n.position?.y || 0) + 50,
              },
              data: {
                ...(n.data || {}),
                nodeId: newId,
                title: n.data?.title ? `${n.data.title} (Copy)` : 'Copy',
              },
              selected: true,
            });
          });

          if (newNodes.length > 0) {
            setNodes([
              ...nodes.map((n) => ({ ...n, selected: false })),
              ...newNodes,
            ]);
            setSelectedNodeId(newNodes[0].id);
            setNotice(`Pasted ${newNodes.length} node${newNodes.length > 1 ? 's' : ''}.`);
            clipboardNodesRef.current = clipboardNodesRef.current.map((n) => ({
              ...n,
              position: {
                x: (n.position?.x || 0) + 30,
                y: (n.position?.y || 0) + 30,
              },
            }));
          }
        }
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') {
        if (selectedNodeId && selectedNode?.type !== 'start') {
          event.preventDefault();
          handleDuplicateNode(selectedNodeId);
        }
        return;
      }

      if (event.key === '/') {
        event.preventDefault();
        openNodePicker({ sourceNodeId: selectedNodeId || startNode?.id || '' });
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [openNodePicker, selectedNodeId, startNode?.id, selectedEdgeId, handleDeleteEdge, handleDeleteNode, handleDuplicateNode, selectedNode?.type, nodes, setNodes, setNotice]);

  /** Purpose: Copy the serialized flow to the clipboard. Why: Exported data must use the same representation as persistence. */
  const handleCopyJson = useCallback(async () => {
    const json = JSON.stringify(getSavePayload(), null, 2);
    await navigator.clipboard.writeText(json);
    setNotice('Flow JSON copied to clipboard.');
  }, [getSavePayload, setNotice]);

  /** Purpose: Download the serialized flow as JSON. Why: A portable snapshot supports inspection and backup without altering the graph. */
  const handleExportJson = useCallback(() => {
    const json = JSON.stringify(getSavePayload(), null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${flowName || 'flow'}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setNotice('Flow JSON exported.');
  }, [flowName, getSavePayload, setNotice]);

  const headerButtonClassName = 'inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200/90 bg-white/95 px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-cyan-400 hover:bg-cyan-50/50 hover:text-cyan-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-200 dark:hover:border-cyan-500/40 dark:hover:text-cyan-200';
  const primaryHeaderButtonClassName = 'inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-cyan-500 px-3 py-1.5 text-xs font-bold text-slate-950 shadow-sm transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-50';
  const accentHeaderButtonClassName = 'inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-2.5 py-1.5 text-xs font-semibold text-fuchsia-700 shadow-sm transition hover:border-fuchsia-400/60 hover:bg-fuchsia-500/20 dark:border-fuchsia-500/30 dark:text-fuchsia-300 disabled:cursor-not-allowed disabled:opacity-50';

  /** Purpose: Update trace, history, language, and variable views from a preview response. Why: Debugger panels must describe the same server execution. */
  const syncDebuggerFromResponse = useCallback((responseData) => {
    if (responseData?.language) {
      debugLanguageRef.current = responseData.language;
    }
    const nextSteps = Array.isArray(responseData?.steps) ? responseData.steps : [];
    const nextLogs = Array.isArray(responseData?.logs) ? responseData.logs : [];
    const nextHistory = Array.isArray(responseData?.history) ? responseData.history : [];
    const nextQuickReplies = Array.isArray(responseData?.quickReplies)
      ? responseData.quickReplies.filter(Boolean)
      : [];
    const nextInputControl = responseData?.inputControl && typeof responseData.inputControl === 'object'
      ? responseData.inputControl
      : null;
    setDebugSteps(nextSteps);
    setDebugLogs(nextLogs);
    setDebugHistory(nextHistory);
    setDebugQuickReplies(nextQuickReplies);
    setDebugInputControl(nextInputControl);
    setDebugVariables(responseData?.variables && typeof responseData.variables === 'object' ? responseData.variables : {});
    setDebugTraceId(responseData?.traceId || '');
    setDebugError(responseData?.error || '');
    const nextIndex = nextSteps.length > 0 ? nextSteps.length - 1 : -1;
    setDebugCurrentStepIndex(nextIndex);
    const nextNodeId = responseData?.waitingForInputNodeId
      || responseData?.currentNodeId
      || nextSteps[nextIndex]?.nodeId
      || '';
    setDebugCurrentNodeId(nextNodeId);
    setPanelTab('debugger');
  }, []);

  /** Purpose: Serialize and persist the flow in its active bot/environment scope. Why: Save status must reflect the actual request and preserve existing routing/configuration contracts. */
  const handleSave = useCallback(async () => {
    if (!selectedBot || !environmentId) {
      setError('Select a bot and load an environment before saving.');
      return false;
    }

    const payload = getSavePayload();
    const normalizedPayload = {
      ...payload,
      botId: selectedBot,
      environmentId,
    };

    setSavingFlow(true);
    setError('');
    try {
      if (storeFlowId) {
        const response = await axios.put(`/api/v1/flows/${storeFlowId}`, normalizedPayload);
        const savedFlow = response.data?.flow;
        if (savedFlow) {
          hydrateFromApi(savedFlow);
          setFlowAfterSave(savedFlow.flowId, savedFlow.botId, savedFlow.environmentId);
        }
        setNotice('Flow updated successfully.');
      } else {
        const response = await axios.post('/api/v1/flows', normalizedPayload);
        const savedFlow = response.data?.flow;
        if (savedFlow) {
          hydrateFromApi(savedFlow);
          setFlowAfterSave(savedFlow.flowId, savedFlow.botId, savedFlow.environmentId);
          navigate(`/flows/${savedFlow.flowId}`, { replace: true });
        }
        setNotice('Flow saved successfully.');
      }
      return true;
    } catch (saveError) {
      console.error('Save failed:', saveError);
      setError(saveError.response?.data?.error || 'Unable to save flow.');
      return false;
    } finally {
      setSavingFlow(false);
    }
  }, [
    selectedBot,
    environmentId,
    getSavePayload,
    storeFlowId,
    setSavingFlow,
    setError,
    hydrateFromApi,
    setFlowAfterSave,
    navigate,
    setNotice,
  ]);

  /** Purpose: Request validation for the saved flow. Why: Release checks need server validation rather than only canvas-level checks. */
  const handleValidateFlow = useCallback(async () => {
    if (!storeFlowId) {
      setError('Save the flow before running validation.');
      return;
    }
    setError('');
    try {
      const { data } = await axios.get(`/api/v1/flows/${storeFlowId}/validate`);
      const validation = data?.validation || {};
      if (!validation.valid) {
        setError(`Flow has ${validation.errors?.length || 0} blocking issue(s): ${validation.errors?.[0]?.message || 'Validation failed.'}`);
        return;
      }
      setNotice(`Flow validation passed${validation.warnings?.length ? ` with ${validation.warnings.length} warning(s)` : ''}.`);
    } catch (validationError) {
      setError(validationError.response?.data?.error || 'Unable to validate flow.');
    }
  }, [storeFlowId, setError, setNotice]);

  /** Purpose: Select and reveal an inspector tab. Why: Opening a tool must also undo the collapsed panel state. */
  const openPanelTab = useCallback((tab) => {
    setPanelTab(tab);
    setInspectorCollapsed(false);
  }, [setInspectorCollapsed]);

  /** Purpose: Send a scoped preview request and synchronize execution results. Why: Testing needs explicit session, start-node, and conversation context. */
  const runDebuggerMessage = useCallback(async (messageText, options = {}) => {
    const {
      reset = false,
      sessionIdOverride = '',
      useDefaultFlow = false,
      payload = {},
      metadata = {},
    } = options || {};

    if (!selectedBot) {
      setDebugError('Select a bot before running the debugger.');
      return null;
    }
    if (!activeFlowId && !useDefaultFlow) {
      setDebugError('Save the flow before running the debugger.');
      return null;
    }

    const text = String(messageText || '').trim();
    if (!text) {
      setDebugError('Type a message to run the debugger.');
      return null;
    }

    const forceDefaultFlow = useDefaultFlow || isHomeCommand(text);
    const shouldReset = reset || forceDefaultFlow;

    const webUid = getOrCreateWebUid();
    const sessionId = sessionIdOverride || debugSessionId || generateSessionId();
    if (!debugSessionId) {
      setDebugSessionId(sessionId);
    }

    setDebugLoading(true);
    setDebugPlaying(false);
    setDebugError('');
    setDebugQuickReplies([]);

    try {
      const response = await axios.post('/api/v1/widget/chat', {
        botId: selectedBot,
        env: selectedEnv || 'dev',
        flowId: forceDefaultFlow ? undefined : activeFlowId,
        uid: webUid,
        userId: webUid,
        sessionId,
        message: text,
        payload: payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {},
        reset: shouldReset,
        debug: true,
        metadata: {
          uid: webUid,
          ...(metadata || {}),
          ...(debugLanguageRef.current ? { preferredLanguage: debugLanguageRef.current, language: debugLanguageRef.current } : {}),
        },
      });

      setDebugSessionId(response.data?.sessionId || sessionId);
      syncDebuggerFromResponse(response.data || {});
      return response.data || null;
    } catch (debugRunError) {
      const errorMessage = debugRunError.response?.data?.error || debugRunError.message || 'Debugger run failed.';
      setDebugError(errorMessage);
      return null;
    } finally {
      setDebugLoading(false);
    }
  }, [activeFlowId, debugSessionId, selectedBot, selectedEnv, syncDebuggerFromResponse]);

  /** Purpose: Fetch logs for the active preview session. Why: Refreshing must not mix traces from another bot or conversation. */
  const handleRefreshDebugLogs = useCallback(async () => {
    if (!selectedBot || !debugSessionId) {
      setDebugError('Run the preview once before refreshing logs.');
      return;
    }

    setDebugLogsRefreshing(true);
    setDebugError('');

    try {
      const response = await axios.post('/api/v1/widget/chat/logs', {
        botId: selectedBot,
        env: selectedEnv || 'dev',
        sessionId: debugSessionId,
      });
      syncDebuggerFromResponse(response.data || {});
    } catch (refreshError) {
      const errorMessage = refreshError.response?.data?.error || refreshError.message || 'Failed to refresh logs.';
      setDebugError(errorMessage);
    } finally {
      setDebugLogsRefreshing(false);
    }
  }, [debugSessionId, selectedBot, selectedEnv, syncDebuggerFromResponse]);

  /** Purpose: Validate and submit debugger input. Why: Empty or overlapping test submissions should not start unintended runs. */
  const handleRunDebug = useCallback(async (submittedValue, payload = {}) => {
    const submittedInput = String(submittedValue ?? debugInput ?? '').trim();
    if (!submittedInput || debugLoading) return;

    const hasExistingDebugRun = debugHistory.length > 0 || debugSteps.length > 0;
    const shouldReset = !hasExistingDebugRun || isHomeCommand(submittedInput);
    setDebugInput('');
    await runDebuggerMessage(submittedInput, {
      reset: shouldReset,
      useDefaultFlow: isHomeCommand(submittedInput),
      payload,
    });
  }, [debugInput, debugHistory, debugLoading, debugSteps, runDebuggerMessage]);

  /** Purpose: Submit a preview quick reply through the debugger. Why: Buttons and typed messages must share the same execution path. */
  const handleQuickReplySelect = useCallback(async (reply) => {
    const selectedReply = String(reply || '').trim();
    if (!selectedReply || debugLoading) return;
    setDebugInput('');
    await runDebuggerMessage(selectedReply);
  }, [debugLoading, runDebuggerMessage]);

  /** Purpose: Return the preview conversation to its default journey. Why: Home testing should preserve the relevant session/language context while resetting navigation state. */
  const handleDebugHome = useCallback(async () => {
    const homeCommand = (localStorage.getItem('preview_home_command') || 'home').trim() || 'home';
    const sessionId = debugSessionId || generateSessionId();
    const preservedLanguage = debugLanguageRef.current || null;
    setReactFlowReady(false);
    setDebugSessionId(sessionId);
    setDebugInput('');
    setDebugCanvasRevision((current) => current + 1);
    await runDebuggerMessage(homeCommand, {
      reset: true,
      sessionIdOverride: sessionId,
      useDefaultFlow: true,
      metadata: preservedLanguage ? { preferredLanguage: preservedLanguage, language: preservedLanguage } : {},
    });
  }, [debugSessionId, runDebuggerMessage]);

  /** Purpose: Start an isolated debug run at a selected node. Why: Targeted testing should not inherit a previous execution's cursor. */
  const handleRunFromHere = useCallback(async (nodeId) => {
    const targetNodeId = nodeId || selectedNodeId;
    if (!targetNodeId) return;
    setDebugCurrentNodeId(targetNodeId);
    openPanelTab('debugger');
    const sessionId = generateSessionId();
    setDebugSessionId(sessionId);
    setDebugInput('');
    setDebugCanvasRevision((current) => current + 1);
    await runDebuggerMessage('/start', {
      reset: true,
      sessionIdOverride: sessionId,
      payload: { startNodeId: targetNodeId },
      metadata: { startNodeId: targetNodeId },
    });
  }, [openPanelTab, runDebuggerMessage, selectedNodeId]);
  runFromHereRef.current = handleRunFromHere;

  /** Purpose: Upload an attachment and continue the preview conversation. Why: The uploaded file must be associated with the correct bot and preview identity. */
  const handleDebugAttachment = useCallback(async (file) => {
    if (!file || !selectedBot || debugLoading) return;
    try {
      setDebugLoading(true);
      const sessionId = debugSessionId || generateSessionId();
      if (!debugSessionId) setDebugSessionId(sessionId);
      const webUid = getOrCreateWebUid();
      const formData = new FormData();
      formData.append('attachment', file);
      formData.append('botId', selectedBot);
      formData.append('env', selectedEnv || 'dev');
      formData.append('sessionId', sessionId);
      formData.append('uid', webUid);
      formData.append('userId', webUid);
      await axios.post('/api/v1/widget/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDebugHistory((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        {
          role: 'user',
          text: file.name,
          mediaUrl: URL.createObjectURL(file),
          mediaType: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'file',
          alt: file.name,
        },
      ]);
    } catch (attachErr) {
      console.error('Failed to upload debug attachment:', attachErr);
      setDebugError(attachErr.response?.data?.error || 'Attachment upload failed.');
    } finally {
      setDebugLoading(false);
    }
  }, [debugLoading, debugSessionId, selectedBot, selectedEnv]);

  /** Purpose: Open the flow preview and prepare its debugger state. Why: Switching from editing must establish a coherent testing view. */
  const handleOpenPreview = useCallback(async () => {
    setReactFlowReady(false);
    setDebugLogsVisible(false);
    setDebugCanvasRevision((current) => current + 1);
    openPanelTab('debugger');
    setDebugHistory([]);
    setDebugLogs([]);
    setDebugSteps([]);
    setDebugQuickReplies([]);
    setDebugCurrentStepIndex(-1);
    setDebugCurrentNodeId('');
    setDebugTraceId('');
    setDebugError('');
    await handleDebugHome();
  }, [handleDebugHome, openPanelTab]);

  useEffect(() => {
    if (!selectedBot) {
      clearHeaderActions();
      return undefined;
    }

    const actions = [
      {
        key: 'flow-debugger',
        label: 'Preview',
        icon: <BugReportIcon sx={{ fontSize: 16 }} />,
        onClick: handleOpenPreview,
        className: accentHeaderButtonClassName,
      },
      {
        key: 'flow-api-management',
        label: 'API Management',
        icon: <ApiIcon sx={{ fontSize: 16 }} />,
        /** Purpose: Navigate to API management. Why: The flow toolbar provides access to reusable API configuration. */
        onClick: () => navigate('/api-management'),
        className: headerButtonClassName,
      },
      {
        key: 'flow-validate',
        label: 'Validate',
        icon: <FactCheckIcon sx={{ fontSize: 16 }} />,
        onClick: handleValidateFlow,
        className: headerButtonClassName,
      },
      {
        key: 'flow-function-management',
        label: 'Functions',
        icon: <FunctionsIcon sx={{ fontSize: 16 }} />,
        /** Purpose: Navigate to function management. Why: Reusable function code is maintained outside individual nodes. */
        onClick: () => navigate('/function-management'),
        className: headerButtonClassName,
      },
      {
        key: 'flow-localization',
        label: 'Localization',
        icon: <TranslateRoundedIcon sx={{ fontSize: 16 }} />,
        /** Purpose: Navigate to localization management. Why: Flow text keys are managed centrally. */
        onClick: () => navigate('/localization'),
        className: headerButtonClassName,
      },
    ];

    if (!isReadOnly) {
      actions.push({
        key: 'flow-edit-details',
        label: 'Edit details',
        icon: <EditOutlinedIcon sx={{ fontSize: 16 }} />,
        onClick: () => {
          setError('');
          setFlowMetaDraft({ name: flowName || '', description: flowDescription || '' });
          setFlowMetaDialogOpen(true);
        },
        className: headerButtonClassName,
      });
      actions.push({
        key: 'flow-save',
        label: savingFlow ? 'Saving…' : 'Save flow',
        icon: <SaveIcon sx={{ fontSize: 16 }} />,
        onClick: handleSave,
        disabled: savingFlow || loadingFlow,
        className: primaryHeaderButtonClassName,
      });
    }

    setHeaderActions({
      flowName: flowName || 'Untitled flow',
      items: actions,
    });

    return () => {
      clearHeaderActions();
    };
  }, [
    selectedBot,
    isReadOnly,
    flowName,
    flowDescription,
    loadingFlow,
    savingFlow,
    handleOpenPreview,
    handleValidateFlow,
    handleSave,
    navigate,
    setHeaderActions,
    clearHeaderActions,
    setError,
    setFlowMetaDialogOpen,
    setFlowMetaDraft,
  ]);

  /** Purpose: Replay earlier user messages in a fresh test session. Why: Sequential replay reproduces the conversation without reusing old server state. */
  const handleReplayConversation = useCallback(async () => {
    const userMessages = debugHistory
      .filter((item) => item?.role === 'user')
      .map((item) => item.text)
      .filter(Boolean);

    if (userMessages.length === 0) {
      setDebugError('No user messages available to replay yet.');
      return;
    }

    const replaySession = `debug-${Date.now()}`;
    setReactFlowReady(false);
    setDebugSessionId(replaySession);
    setDebugLoading(true);
    setDebugPlaying(false);
    setDebugError('');
    setDebugSteps([]);
    setDebugLogs([]);
    setDebugHistory([]);
    setDebugCurrentStepIndex(-1);
    setDebugCurrentNodeId('');

    try {
      let latestResponse = null;
      for (let index = 0; index < userMessages.length; index += 1) {
        // eslint-disable-next-line no-await-in-loop
        latestResponse = await runDebuggerMessage(userMessages[index], {
          reset: index === 0,
          sessionIdOverride: replaySession,
        });
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 250));
      }

      if (latestResponse) {
        syncDebuggerFromResponse(latestResponse);
      }
      openPanelTab('debugger');
      setDebugCanvasRevision((current) => current + 1);
    } finally {
      setDebugLoading(false);
    }
  }, [debugHistory, runDebuggerMessage, syncDebuggerFromResponse, openPanelTab]);

  /** Purpose: Toggle the debug log view and restore the canvas when returning. Why: The graph needs a fresh layout after its container becomes visible. */
  const handleToggleDebugLogs = useCallback(() => {
    setDebugLogsVisible((current) => {
      const next = !current;
      if (!next) {
        setReactFlowReady(false);
        setDebugCanvasRevision((revision) => revision + 1);
      }
      return next;
    });
  }, []);

  /** Purpose: Reset the active preview debugger. Why: A new test should not retain old execution state. */
  const handleResetDebug = useCallback(() => {
    resetDebugger();
  }, [resetDebugger]);

  /** Purpose: Reload the editor after confirming unsaved changes can be discarded. Why: Refresh must not silently lose the user's edits. */
  const handleRefresh = useCallback(() => {
    if (editorHistory.dirty && !window.confirm('Reload and discard unsaved flow changes?')) return;
    initBuilder();
  }, [initBuilder, editorHistory.dirty]);

  /** Purpose: Update flow-level metadata. Why: Metadata edits should share the graph store's persistence state. */
  const handleFlowMetaChange = useCallback((patch) => {
    setFlowData(patch);
  }, [setFlowData]);

  /** Purpose: save updated flow metadata with the current graph. Why: name and description stay editable after creation and use the same persistence contract as canvas edits. */
  const handleSaveFlowDetails = useCallback(async (event) => {
    event.preventDefault();
    const name = String(flowMetaDraft.name || '').trim();
    if (!name) {
      setError('Flow name is required.');
      return;
    }
    setFlowData({ flowName: name, flowDescription: String(flowMetaDraft.description || '').trim() });
    const saved = await handleSave();
    if (saved) setFlowMetaDialogOpen(false);
  }, [flowMetaDraft, setError, setFlowData, handleSave, setFlowMetaDialogOpen]);

  /** Purpose: Fit the viewport to the graph. Why: Users need a reliable way to recover the full canvas view. */
  const handleFitView = useCallback(() => {
    reactFlowInstanceRef.current?.fitView({
      padding: 0.2,
      duration: 260,
      minZoom: 0.45,
      maxZoom: 1.15,
    });
  }, []);

  /** Purpose: Capture the React Flow instance and schedule initial fitting. Why: Viewport operations require a ready canvas instance. */
  const handleReactFlowInit = useCallback((instance) => {
    reactFlowInstanceRef.current = instance;
    setReactFlowReady(true);

    if (panelTab === 'debugger' && !debugLogsVisible) {
      requestAnimationFrame(() => {
        instance.fitView({
          padding: 0.2,
          duration: 280,
          minZoom: 0.45,
          maxZoom: 1.15,
        });
      });
    }
  }, [debugLogsVisible, panelTab]);

  useEffect(() => {
    if (panelTab !== 'debugger' || debugLogsVisible || !reactFlowReady || nodes.length === 0) {
      return;
    }

    /** Purpose: Fit the graph after the layout transition. Why: Measurements taken before the container settles can produce an incorrect zoom. */
    const timer = setTimeout(() => {
      handleFitView();
    }, 100);

    return () => clearTimeout(timer);
  }, [panelTab, debugLogsVisible, debugCanvasRevision, reactFlowReady, nodes.length, handleFitView]);

  /** Purpose: Render the shared React Flow canvas with editor handlers. Why: Editor modes must retain the same graph interactions and node definitions. */
  const renderFlowCanvas = (canvasKey = activeFlowId || 'new-flow-canvas') => (
    <ReactFlow
      key={canvasKey}
      className="!h-full !w-full"
      proOptions={{ hideAttribution: true }}
      nodes={displayNodes}
      edges={displayEdges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onInit={handleReactFlowInit}
      onNodesChange={isReadOnly ? undefined : handleNodeChanges}
      onEdgesChange={isReadOnly ? undefined : handleEdgeChanges}
      onConnect={isReadOnly ? undefined : handleConnect}
      isValidConnection={isValidConnection}
      onEdgeUpdate={isReadOnly ? undefined : handleEdgeReconnect}
      onEdgeClick={handleEdgeClick}
      onEdgeMouseEnter={(_, edge) => setHoverBranch(edge)}
      onEdgeMouseLeave={() => setHoverBranch(null)}
      onDrop={isReadOnly ? undefined : handleDrop}
      onDragOver={isReadOnly ? undefined : handleDragOver}
      onNodeClick={handleNodeClick}
      onPaneClick={handlePaneClick}
      fitView={false}
      autoPanOnNodeDrag={false}
      snapToGrid={true}
      snapGrid={[20, 20]}
      nodeDragThreshold={1}
      minZoom={0.2}
      maxZoom={2}
      nodesDraggable={!isReadOnly}
      nodesConnectable={!isReadOnly}
      edgesUpdatable={!isReadOnly}
      selectNodesOnDrag={!isReadOnly}
      connectionRadius={32}
      edgeUpdaterRadius={28}
      connectionLineStyle={{ stroke: '#0ea5e9', strokeWidth: 3 }}
      defaultEdgeOptions={{
        type: 'premium',
        animated: true,
        style: { stroke: '#67e8f9', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed },
        interactionWidth: 28,
      }}
    >
      <Background
        variant="dots"
        gap={20}
        size={1}
        color="rgba(148,163,184,0.18)"
      />
      <div className="nodrag nopan absolute left-24 right-4 top-3 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 p-2 text-xs text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
        <input aria-label="Search flow nodes" placeholder="Find node, text, key or function…" value={canvasSearch} onChange={(event) => setCanvasSearch(event.target.value)} className="w-56 rounded-lg border border-slate-200 bg-transparent px-2 py-2" />
        <select aria-label="Preview language" value={previewLanguage} onChange={(event) => setPreviewLanguage(event.target.value)} className="rounded-lg border bg-transparent p-2">
          <option value="">Show mapped keys</option>
          {(canvasLocalization?.enabledLanguages || []).map((code) => <option key={code} value={code}>Preview: {code}</option>)}
        </select>
        <button disabled={!editorHistory.canUndo || isReadOnly} onClick={editorHistory.undo} className="rounded border px-2 py-2 disabled:opacity-40" title="Undo (Ctrl/Cmd Z)">↶ Undo</button>
        <button disabled={!editorHistory.canRedo || isReadOnly} onClick={editorHistory.redo} className="rounded border px-2 py-2 disabled:opacity-40" title="Redo (Ctrl/Cmd Shift Z)">↷ Redo</button>
        <button
          type="button"
          onClick={handleTidyUpLayout}
          disabled={isReadOnly || nodes.length === 0}
          className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-2 font-medium text-slate-700 shadow-2xs hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/60"
          title="Auto-arrange and tidy up flow canvas nodes"
        >
          <span>✨ Tidy Up</span>
        </button>
        <button
          type="button"
          onClick={() => setAnalyticsMode((prev) => !prev)}
          className={`flex items-center gap-1 rounded-md border px-2.5 py-2 font-medium shadow-2xs transition ${
            analyticsMode
              ? 'border-cyan-400 bg-cyan-50 font-bold text-cyan-800 dark:border-cyan-500/40 dark:bg-cyan-950/40 dark:text-cyan-200'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/60'
          }`}
          title="Toggle Canvas Drop-off Heatmap and visitor flow analytics"
        >
          <span>📊 {analyticsMode ? 'Heatmap: On' : 'Analytics Heatmap'}</span>
        </button>
        <span role="status" className={editorHistory.dirty ? 'text-amber-600' : 'text-emerald-600'}>{savingFlow ? 'Saving…' : editorHistory.dirty ? '● Unsaved changes' : storeFlowId ? '✓ Saved' : 'Not saved yet'}</span>
        {canvasSearch.trim() && <div className="max-h-48 w-full overflow-auto border-t pt-2">
          {searchFlowNodes(nodes, canvasSearch, savedFunctions).map((node) => <button key={node.id} className="block w-full rounded p-2 text-left hover:bg-sky-50 dark:hover:bg-slate-800" onClick={() => { setSelectedNodeId(node.id); reactFlowInstanceRef.current?.fitView({ nodes: [{ id: node.id }], duration: 300, maxZoom: 1.1 }); setCanvasSearch(''); }}>{node.data?.title || node.id} <span className="text-slate-400">{node.data?.libraryKey}</span></button>)}
        </div>}
      </div>
      <div className="absolute left-5 top-8 z-20 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => openPanelTab('variables')}
          title="Variables"
          aria-label="Open variables"
          className={[
            'grid h-14 w-14 place-items-center rounded-full border border-slate-200 bg-white text-base font-bold text-indigo-600 shadow-[0_12px_30px_rgba(15,23,42,0.12)] transition',
            'hover:-translate-y-0.5 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700',
            'dark:border-white/10 dark:bg-slate-950 dark:text-indigo-200 dark:hover:border-indigo-400/50 dark:hover:bg-indigo-500/10',
            panelTab === 'variables' ? 'ring-2 ring-indigo-400/50' : '',
          ].join(' ')}
        >
          {'{x}'}
        </button>
      </div>
      <Controls
        position="bottom-left"
        className="!bg-white/90 !text-slate-700 dark:!bg-slate-900/95 dark:!text-slate-200"
      />
      <MiniMap
        nodeColor={(node) => getNodeStyle(node.type)}
        maskColor="rgba(2,6,23,0.55)"
        className="!rounded-2xl !border !border-slate-200 !bg-white/90 dark:!border-white/10 dark:!bg-slate-900/95"
      />

      {nodes.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="max-w-md rounded-3xl border border-dashed border-slate-200 bg-white/90 px-6 py-5 text-center text-sm text-slate-500 shadow-sm dark:border-white/10 dark:bg-slate-950/80 dark:text-slate-400">
            Drag a node from the library to start building the journey.
          </div>
        </div>
      )}
    </ReactFlow>
  );

  /** Purpose: Select a debugger execution step and its node. Why: Trace navigation should keep the canvas and details panel aligned. */
  const handleStepSelect = useCallback((index) => {
    setDebugCurrentStepIndex(index);
    const step = debugSteps[index];
    if (step?.nodeId) {
      setDebugCurrentNodeId(step.nodeId);
    }
  }, [debugSteps]);

  /** Purpose: Move back one execution step and pause playback. Why: Manual navigation must remain bounded and not race automatic playback. */
  const handleStepBack = useCallback(() => {
    setDebugCurrentStepIndex((current) => {
      const nextIndex = Math.max(current - 1, 0);
      const step = debugSteps[nextIndex];
      if (step?.nodeId) {
        setDebugCurrentNodeId(step.nodeId);
      }
      return nextIndex;
    });
    setDebugPlaying(false);
  }, [debugSteps]);

  /** Purpose: Move forward one execution step and pause playback. Why: The debugger must not move past the final trace entry. */
  const handleStepForward = useCallback(() => {
    setDebugCurrentStepIndex((current) => {
      const nextIndex = Math.min(current + 1, debugSteps.length - 1);
      const step = debugSteps[nextIndex];
      if (step?.nodeId) {
        setDebugCurrentNodeId(step.nodeId);
      }
      return nextIndex;
    });
    setDebugPlaying(false);
  }, [debugSteps]);

  /** Purpose: Toggle playback when execution steps exist. Why: An empty trace has nothing to animate. */
  const handleToggleDebugPlay = useCallback(() => {
    if (!debugSteps.length) return;
    setDebugPlaying((current) => !current);
  }, [debugSteps.length]);

  /** Purpose: Resolve a readable label for the current debug node. Why: Execution details should identify the step without requiring a raw ID. */
  const selectedDebugNodeLabel = useMemo(() => {
    if (!debugCurrentNodeId) return '';
    return nodes.find((node) => node.id === debugCurrentNodeId)?.data?.title || '';
  }, [debugCurrentNodeId, nodes]);

  if (loadingFlow && !nodes.length) {
    return (
      <div className="flex h-full min-h-[70vh] items-center justify-center rounded-[32px] border border-white/10 bg-slate-950/80 text-slate-200">
        <div className="space-y-3 text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-cyan-400/40 border-t-cyan-300" />
          <div className="text-lg font-semibold">Loading flow builder…</div>
          <div className="text-sm text-slate-500">Preparing canvas and backend data</div>
        </div>
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div className="flex h-full min-h-0 flex-col gap-2 overflow-hidden text-slate-900 dark:text-slate-100">
        {isReadOnly && (
          <div className="rounded-2xl border border-cyan-300/40 bg-cyan-50 px-4 py-3 text-sm text-cyan-900 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100">
            PROD is read-only. Edit DEV and deploy through the Release Center.
          </div>
        )}
        {(error || notice) && (
          <div className="rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <span className={error ? 'text-rose-500 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}>
              {error || notice}
            </span>
          </div>
        )}

        {panelTab === 'debugger' ? (
          <div
            className="flex min-h-0 flex-1 gap-4 overflow-hidden rounded-[24px] border border-slate-200/80 bg-white/80 p-3 shadow-[0_12px_36px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-950/70 dark:shadow-2xl dark:shadow-slate-950/50"
          >
            <div className="h-full min-h-0 flex-1 overflow-hidden">
              {debugLogsVisible ? (
                <DebugConsolePane
                  logs={debugLogs}
                  steps={debugSteps}
                  sessionId={debugSessionId}
                  logHeight={debugLogHeight}
                  onRefreshLogs={handleRefreshDebugLogs}
                  logsRefreshing={debugLogsRefreshing}
                />
              ) : (
                <div className="h-full min-h-0 overflow-hidden rounded-[24px] border border-slate-200/80 bg-white/80 shadow-[0_12px_36px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-950/70 dark:shadow-2xl dark:shadow-slate-950/50">
                  {renderFlowCanvas(`debugger-flow-canvas-hidden-${debugCanvasRevision}-${debugSessionId || 'pending'}-${debugLogsVisible ? 'logs' : 'canvas'}`)}
                </div>
              )}
            </div>

            <div className="h-full w-[340px] min-w-[300px] max-w-[380px] overflow-hidden lg:w-[360px]">
              <FlowDebuggerPanel
                sessionId={debugSessionId}
                traceId={debugTraceId}
                botLabel={botLabel}
                botName={botDisplayName}
                widgetConfig={environmentConfig}
                history={debugHistory}
                quickReplies={debugQuickReplies}
                variables={debugVariables}
                logsVisible={debugLogsVisible}
                onToggleLogs={handleToggleDebugLogs}
                debugInput={debugInput}
                onDebugInputChange={setDebugInput}
                onRunDebug={handleRunDebug}
                onQuickReply={handleQuickReplySelect}
                onHomeDebug={handleDebugHome}
                onReplayConversation={handleReplayConversation}
                onResetDebug={handleResetDebug}
                onAttachment={handleDebugAttachment}
                loading={debugLoading}
                inputControl={debugInputControl}
                waitingForUserInput={Boolean(selectedDebugStep?.status === 'waiting_for_input')}
                error={debugError}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden rounded-[24px] border border-slate-200/80 bg-white/80 shadow-[0_12px_36px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-950/70 dark:shadow-2xl dark:shadow-slate-950/50">
            <main className="relative min-h-0 min-w-0 flex-1 overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.94),_transparent_24%),linear-gradient(180deg,_#fcfcfe_0%,_#f5f7fb_100%)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.15),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(139,92,246,0.14),_transparent_24%),linear-gradient(180deg,_#07111f_0%,_#04070d_100%)]">
              {dynamicChatFullscreen && selectedNodeIsDynamicChat ? <DynamicChatWorkspace
                node={selectedNode}
                variables={variables}
                onPromptChange={(value) => handleNodeUpdate(selectedNode.id, { data: { config: { message: value, prompt: value } } })}
                onOpenSettings={() => setDynamicChatSettingsOpen(true)}
                onSave={handleSave}
                onBackToFlow={() => { setDynamicChatSettingsOpen(false); setDynamicChatFullscreen(false); setInspectorCollapsed(true); }}
                saving={savingFlow}
                readOnly={isReadOnly}
              /> : renderFlowCanvas()}
            </main>

            {(!dynamicChatFullscreen || dynamicChatSettingsOpen) ? <div className="hidden lg:flex">
              <FlowPropertiesPanel
                fullscreen={false}
                workspaceSettings={dynamicChatFullscreen && dynamicChatSettingsOpen}
                onToggleFullscreen={() => dynamicChatFullscreen ? setDynamicChatSettingsOpen(false) : setDynamicChatFullscreen(true)}
                onSaveFlow={handleSave}
                isReadOnly={isReadOnly}
                collapsed={dynamicChatFullscreen ? false : inspectorCollapsed}
                onToggle={() => setInspectorCollapsed(!inspectorCollapsed)}
                activeTab={panelTab}
                onTabChange={openPanelTab}
                flowName={flowName}
                flowDescription={flowDescription}
                variables={variables}
                onFlowChange={handleFlowMetaChange}
                onVariablesChange={setVariables}
                onAddVariable={addVariable}
                onUpdateVariable={updateVariable}
                onDeleteVariable={removeVariable}
                selectedNode={selectedNode}
                selectedEdge={selectedEdge}
                nodes={nodes}
                edges={edges}
                onNodeChange={handleNodeUpdate}
                onReorderQuickReplyOptions={isReadOnly ? undefined : reorderQuickReplyOptions}
                onEdgeChange={handleEdgeUpdate}
                onDeleteNode={handleDeleteNode}
                onDeleteEdge={handleDeleteEdge}
                onCreateEdge={(source, target, patch = {}) => {
                  /** Purpose: Resolve the picker connection's source node. Why: Creation controls need the correct source configuration. */
                  const sourceNode = nodes.find((node) => node.id === source);
                  const handle = patch.sourceHandle || '';
                  const baseEdge = buildEdge({ source, target, sourceHandle: handle, sourceNode, edges });
                  const edge = {
                    ...baseEdge,
                    ...patch,
                    data: {
                      ...(baseEdge.data || {}),
                      ...(patch.data || {}),
                    },
                  };
                  // Keep node-picker connections consistent with drag connections:
                  // legacy Dynamic Chat edges can have a missing/old handle while
                  // still representing the same `success` outcome. Replacing by
                  // outcome prevents a second success route from reaching preflight.
                  const apiBranch = getApiEdgeBranch(sourceNode, edge);
                  const handoffBranch = getHandoffEdgeBranch(sourceNode, edge, edges);
                  const branch = apiBranch || handoffBranch;
                  const retainedEdges = branch
                    ? edges.filter((candidate) => (
                      candidate.source !== source
                      || (
                        candidate.sourceHandle !== branch.sourceHandle
                        && String(candidate.data?.condition || candidate.label || '').trim().toLowerCase() !== branch.condition
                      )
                    ))
                    : handle
                      ? edges.filter((candidate) => !(candidate.source === source && (candidate.sourceHandle || '') === handle))
                      : edges;
                  setEdges([...retainedEdges, edge]);
                  setSelectedEdgeId(edge.id);
                }}
                onClearSelection={clearSelection}
                onClearEdgeSelection={() => setSelectedEdgeId('')}
                onCopyJson={handleCopyJson}
                onExportJson={handleExportJson}
                onFitView={handleFitView}
                onRefresh={handleRefresh}
                saving={savingFlow}
                loading={loadingFlow || environmentLoading}
                isNewFlow={isNewFlow}
                customTables={customTables}
                savedApis={savedApis}
                savedFunctions={savedFunctions}
                availableFlows={availableFlows}
                currentFlowId={storeFlowId || flowId || ''}
                currentBotId={selectedBot || ''}
                currentEnvironment={selectedEnv || 'dev'}
                debuggerProps={{
                  sessionId: debugSessionId,
                  traceId: debugTraceId,
                  botLabel,
                  botName: botDisplayName,
                  widgetConfig: environmentConfig,
                  steps: debugSteps,
                  logs: debugLogs,
                  history: debugHistory,
                  quickReplies: debugQuickReplies,
                  variables: debugVariables,
                  inputControl: debugInputControl,
                  currentStepIndex: debugCurrentStepIndex,
                  currentStep: selectedDebugStep,
                  currentNodeId: debugCurrentNodeId,
                  currentNodeLabel: selectedDebugNodeLabel,
                  debugInput,
                  onDebugInputChange: setDebugInput,
                  onRunDebug: handleRunDebug,
                  onQuickReply: handleQuickReplySelect,
                  onHomeDebug: handleDebugHome,
                  onReplayConversation: handleReplayConversation,
                  onResetDebug: handleResetDebug,
                  onStepSelect: handleStepSelect,
                  onStepBack: handleStepBack,
                  onStepForward: handleStepForward,
                  onTogglePlay: handleToggleDebugPlay,
                  playing: debugPlaying,
                  loading: debugLoading,
                  waitingForUserInput: Boolean(selectedDebugStep?.status === 'waiting_for_input'),
                  error: debugError,
                }}
              />
            </div> : null}
          </div>
        )}

        <NodeSelectionDrawer
          open={nodePickerOpen}
          search={nodePickerSearch}
          onSearchChange={setNodePickerSearch}
          activeCategory={nodePickerCategory}
          onCategoryChange={setNodePickerCategory}
          onClose={() => setNodePickerOpen(false)}
          onSelect={handleSelectNodeTemplate}
          recentItems={recentNodeItems}
        />
        {flowMetaDialogOpen ? (
          <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm" onMouseDown={(event) => { if (event.target === event.currentTarget && !savingFlow) setFlowMetaDialogOpen(false); }}>
            <form role="dialog" aria-modal="true" aria-labelledby="flow-details-title" onSubmit={handleSaveFlowDetails} className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-slate-900">
              <div className="mb-5">
                <h2 id="flow-details-title" className="text-lg font-bold text-slate-900 dark:text-white">Edit flow details</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Update the name and description shown in your journey list.</p>
              </div>
              {error ? <div role="alert" className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200">{error}</div> : null}
              <label className="mb-4 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Flow name
                <input autoFocus required value={flowMetaDraft.name} onChange={(event) => setFlowMetaDraft((current) => ({ ...current, name: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-white/15 dark:bg-slate-950 dark:text-white" placeholder="e.g. Customer support" />
              </label>
              <label className="mb-6 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Description <span className="font-normal text-slate-400">(optional)</span>
                <textarea rows={3} value={flowMetaDraft.description} onChange={(event) => setFlowMetaDraft((current) => ({ ...current, description: event.target.value }))} className="mt-1.5 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-white/15 dark:bg-slate-950 dark:text-white" placeholder="What this journey is for" />
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" disabled={savingFlow} onClick={() => setFlowMetaDialogOpen(false)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/5">Cancel</button>
                <button type="submit" disabled={savingFlow || loadingFlow || !flowMetaDraft.name.trim()} className="rounded-xl bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50">{savingFlow ? 'Saving…' : 'Save details'}</button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </ReactFlowProvider>
  );
}
